/**
 * VendasPage Component
 * 
 * Página de criação e gerenciamento de vendas.
 * Interface similar a PDV com busca de produtos, carrinho e finalização.
 * 
 * Funcionalidades:
 * - Busca de produtos
 * - Seleceção de Pré Venda realizada
 * - Seleção de vendedor e tabela de preços
 * - Finalização de venda (APENAS este botão)
 * - Modal obrigatório de abertura de caixa
 * - Modal de forma de recebimento ao finalizar
 * - Modal de impressão após pagamento
 * - Altera estoque ao finalizar
 */

import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  X, CheckCircle2, Clock, Calculator, Database, FileText,
  Box, Loader2, ChevronDown, LogOut,
  History, Printer, ShieldCheck, ArrowUpRight, Info,
  Lock, Receipt, Wallet, Banknote, CreditCard, Smartphone, CalendarClock,
  PlusCircle, Trash2, Check, RefreshCcw, ScanBarcode, Package, AlertTriangle
} from 'lucide-react'
import { useAuth } from '../../../../contexts/AuthContext'
import CashRegisterModal from '../../../../components/ERP/CashRegisterModal/CashRegisterModal'
import PaymentConfirmationModal from '../../../../components/ERP/PaymentConfirmationModal/PaymentConfirmationModal'
import PrintModal from '../../../../components/ERP/PrintModal/PrintModal'
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal'
import CrediarioPaymentModal from './CrediarioPaymentModal'
import {
  resolveCustomerForCaixa,
  getEffectiveCustomerId
} from './saleCustomerUtils'
import { buildNfePaymentsFromApplied } from '../../../../utils/nfePaymentsFromSale'
import { 
  cashRegisterService, 
  saleService, 
  customerService,
  paymentReceiptMethodService,
  customerCreditService
} from '../../../../services/erpApi'
import thermalPrintService from '../../../../services/thermalPrintService'
import { buildCashRegisterClosingReceipt } from '../../../../utils/cashRegisterClosingReceipt'
import './VendasPage.css'

const parseMoneyBR = (value) => {
  if (value === undefined || value === null || value === '') return 0
  const cleaned = String(value).replace(/\./g, '').replace(',', '.')
  const parsed = parseFloat(cleaned)
  return Number.isNaN(parsed) ? 0 : parsed
}

const formatMoneyInputBR = (value) => {
  const num = typeof value === 'number' ? value : parseFloat(value) || 0
  return num.toFixed(2).replace('.', ',')
}

const MONEY_EPSILON = 0.01

const buildClosingPaymentLines = (actualAmountStr, paymentMethods, paymentAmounts) => {
  const lines = []
  const cashAmount = parseMoneyBR(actualAmountStr)
  if (cashAmount > 0) {
    lines.push({ description: 'Dinheiro', amount: cashAmount })
  }

  paymentMethods.forEach((method) => {
    const raw = paymentAmounts[method.payment_receipt_method_id]
    const amount = parseMoneyBR(raw)
    if (amount > 0) {
      lines.push({
        description: method.payment_method_name || 'Forma de pagamento',
        amount
      })
    }
  })

  return lines
}

const isCrediarioMethod = (method) => {
  if (!method) return false
  const type = (method.payment_type || '').toUpperCase()
  const name = (method.name || '').toLowerCase()
  return (
    type === 'CREDIÁRIO' ||
    type === 'CREDIARIO' ||
    name.includes('crediário') ||
    name.includes('crediario')
  )
}

const isCustomerCreditMethod = (method) => {
  if (!method) return false
  const type = (method.payment_type || '').toUpperCase()
  const name = (method.name || '').toLowerCase()
  const provider = (method.provider || '').toUpperCase()
  return (
    type === 'CRÉDITO_BÔNUS' ||
    type === 'CREDITO_BONUS' ||
    type === 'CRÉDITO_BONUS' ||
    (provider === 'INTERNO' && (name.includes('crédito') || name.includes('credito')) &&
      (name.includes('bônus') || name.includes('bonus'))) ||
    ((name.includes('crédito') || name.includes('credito')) &&
      (name.includes('bônus') || name.includes('bonus')))
  )
}

const printClosingReceipt = async (receiptParams) => {
  const texto = buildCashRegisterClosingReceipt(receiptParams)
  try {
    if (thermalPrintService.isWebUSBSupported()) {
      await thermalPrintService.imprimir(texto)
    } else {
      thermalPrintService.imprimirTextoSimples(texto)
    }
  } catch (printError) {
    console.error('Erro na impressão térmica do fechamento:', printError)
    thermalPrintService.imprimirTextoSimples(texto)
  }
}

function VendasPage() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  
  // Refs para dropdowns
  const userMenuRef = useRef(null)
  const onlineOrderInfoRef = useRef(null)
  const countInputRefs = useRef({})
  const pendingCountFocusRef = useRef(null)
  
  // Estados do caixa
  const [cashRegisterModalOpen, setCashRegisterModalOpen] = useState(false)
  const [cashRegisterStatus, setCashRegisterStatus] = useState(null)
  const [checkingCashRegister, setCheckingCashRegister] = useState(true)
  const [closeCashRegisterModalOpen, setCloseCashRegisterModalOpen] = useState(false)
  const [actualAmount, setActualAmount] = useState('')
  const [closingCashRegister, setClosingCashRegister] = useState(false)
  const [paymentMethods, setPaymentMethods] = useState([])
  const [paymentAmounts, setPaymentAmounts] = useState({}) // { payment_receipt_method_id: value }
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false)

  // Estados do pedido
  const [selectedProducts, setSelectedProducts] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  /** ID do cliente da pré-venda (fallback se getById do cliente falhar) */
  const [saleCustomerId, setSaleCustomerId] = useState(null)
  const [observations, setObservations] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingStep, setLoadingStep] = useState('')
  const [countedQuantities, setCountedQuantities] = useState({}) // Quantidades contadas pelo usuário { productId: quantity }
  const [barcodeInputs, setBarcodeInputs] = useState({}) // Valor digitado/scaneado { productId: string }
  const [barcodeValidated, setBarcodeValidated] = useState({}) // Código validado { productId: boolean }
  const [currentPresaleId, setCurrentPresaleId] = useState(null) // ID da pré-venda atual sendo processada
  const [currentPresaleNumber, setCurrentPresaleNumber] = useState(null) // Número da pré-venda atual sendo processada
  const [currentSellerId, setCurrentSellerId] = useState(null) // Vendedor da pré-venda (comissão/métricas)
  const [onlineOrderPaid, setOnlineOrderPaid] = useState(false)
  const [onlineOrderNumber, setOnlineOrderNumber] = useState(null)
  const [onlineOrderId, setOnlineOrderId] = useState(null)
  const [onlinePaymentMethod, setOnlinePaymentMethod] = useState(null)
  const [onlineInstallments, setOnlineInstallments] = useState(1)
  const [onlinePagseguroTxId, setOnlinePagseguroTxId] = useState(null)
  const [onlinePaymentPendingLocal, setOnlinePaymentPendingLocal] = useState(false)
  const [onlineOrderInfoOpen, setOnlineOrderInfoOpen] = useState(false)

  // Estado Global do Fluxo
  const [step, setStep] = useState('counting') // 'counting' | 'payment' | 'success'
  
  // Estados dos modais
  const [paymentConfirmationModalOpen, setPaymentConfirmationModalOpen] = useState(false)
  const [pendingPaymentData, setPendingPaymentData] = useState(null)
  const [printModalOpen, setPrintModalOpen] = useState(false)
  const [printModalInstance, setPrintModalInstance] = useState(0)
  const [completedSale, setCompletedSale] = useState(null)

  const openPrintModal = () => {
    setPrintModalInstance((n) => n + 1)
    setPrintModalOpen(true)
  }
  const [waitingModalOpen, setWaitingModalOpen] = useState(false)
  const [waitingPresales, setWaitingPresales] = useState([])
  const [loadingWaiting, setLoadingWaiting] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [paymentTypeModalOpen, setPaymentTypeModalOpen] = useState(false)
  
  // Estados de Pagamento
  const [appliedPayments, setAppliedPayments] = useState([])
  const [customerCreditBalance, setCustomerCreditBalance] = useState(0)
  const [currentAmountInput, setCurrentAmountInput] = useState('')
  const [selectedPaymentType, setSelectedPaymentType] = useState(null) // 'DINHEIRO', 'DÉBITO', 'CRÉDITO_À_VISTA', 'CRÉDITO_PARCELADO'
  const [selectedMethodId, setSelectedMethodId] = useState(null) // ID da forma de recebimento selecionada (quando já existe)
  const [systemPaymentMethods, setSystemPaymentMethods] = useState([]) // Formas de recebimento do sistema (lista plana)
  const [paymentMethodsHierarchy, setPaymentMethodsHierarchy] = useState(null) // Formas agrupadas por hierarquia
  const [loadingSystemPaymentMethods, setLoadingSystemPaymentMethods] = useState(false)
  const [cardInstallments, setCardInstallments] = useState(1)
  const [selectedBrand, setSelectedBrand] = useState(null) // Bandeira só para cartão (MASTER, VISA, etc)
  const [crediarioModalOpen, setCrediarioModalOpen] = useState(false)
  const [pendingCrediarioPayment, setPendingCrediarioPayment] = useState(null)
  const [cashChangeModalOpen, setCashChangeModalOpen] = useState(false)
  const [cashReceivedInput, setCashReceivedInput] = useState('')
  const [cashReceived, setCashReceived] = useState(null) // valor confirmado do cliente (número) ou null
  
  // Estados do modal de confirmação
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: null,
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
    showCancel: true
  })

  // Verificar status do caixa ao carregar
  useEffect(() => {
    checkCashRegisterStatus()
  }, [])

  // Debug: verificar quando produtos são atualizados
  useEffect(() => {
    console.log('selectedProducts atualizado:', selectedProducts)
  }, [selectedProducts])

  // Carregar formas de pagamento quando o modal de fechamento abrir
  useEffect(() => {
    const loadPaymentMethods = async () => {
      if (closeCashRegisterModalOpen && cashRegisterStatus?.cashRegister?.id) {
        setLoadingPaymentMethods(true)
        try {
          const response = await cashRegisterService.getPaymentMethods(cashRegisterStatus.cashRegister.id)
          if (response.success) {
            // Filtrar "Dinheiro" da lista, pois já temos campo específico para isso
            const filteredMethods = (response.data || []).filter(
              method => !method.payment_method_name || 
              !method.payment_method_name.toLowerCase().includes('dinheiro')
            )
            setPaymentMethods(filteredMethods)
            // Inicializar valores vazios (apenas para métodos filtrados)
            const initialAmounts = {}
            filteredMethods.forEach(method => {
              initialAmounts[method.payment_receipt_method_id] = ''
            })
            setPaymentAmounts(initialAmounts)
          }
        } catch (error) {
          console.error('Erro ao carregar formas de pagamento:', error)
          setPaymentMethods([])
        } finally {
          setLoadingPaymentMethods(false)
        }
      }
    }
    loadPaymentMethods()
  }, [closeCashRegisterModalOpen, cashRegisterStatus?.cashRegister?.id])

  const checkCashRegisterStatus = async () => {
    try {
      setCheckingCashRegister(true)
      const response = await cashRegisterService.checkOpenStatus()
      
      if (response.success) {
        setCashRegisterStatus(response.data)
        
        // Se não houver caixa aberto, abrir modal obrigatório
        if (!response.data.isOpen) {
          setCashRegisterModalOpen(true)
        }
      }
    } catch (error) {
      console.error('Erro ao verificar status do caixa:', error)
    } finally {
      setCheckingCashRegister(false)
    }
  }

  const handleCashRegisterOpened = (cashRegister) => {
    setCashRegisterStatus({
      isOpen: true,
      cashRegister
    })
    setCashRegisterModalOpen(false)
  }

  const handleExitFromModal = () => {
    navigate('/erp/dashboard')
  }

  // Helper para mostrar mensagens
  const showMessage = (title, message, type = 'info', onConfirm = null) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: onConfirm || (() => setConfirmModal(prev => ({ ...prev, isOpen: false }))),
      confirmText: 'OK',
      cancelText: 'Cancelar',
      showCancel: !!onConfirm
    })
  }


  // Função para formatar input de valores monetários
  const handlePaymentAmountInputChange = (paymentMethodId, e) => {
    let inputValue = e.target.value
    
    // Remove tudo exceto números e vírgula
    let cleaned = inputValue.replace(/[^\d,]/g, '')
    
    // Garantir apenas uma vírgula
    const parts = cleaned.split(',')
    let formatted = parts[0] || ''
    
    // Se tiver vírgula, adicionar parte decimal (máximo 2 dígitos)
    if (parts.length > 1) {
      formatted += ',' + parts.slice(1).join('').substring(0, 2)
    }
    
    // Atualizar estado
    setPaymentAmounts(prev => ({
      ...prev,
      [paymentMethodId]: formatted
    }))
    
    // Atualizar o valor do input
    e.target.value = formatted
  }

  // Fechar caixa
  const handleCloseCashRegister = async () => {
    if (!actualAmount || parseMoneyBR(actualAmount) <= 0) {
      alert('Por favor, informe o valor atual do dinheiro no caixa')
      return
    }

    if (!cashRegisterStatus?.cashRegister?.id) {
      alert('Erro: Caixa não encontrado')
      return
    }

    setClosingCashRegister(true)
    try {
      const amount = parseMoneyBR(actualAmount)
      const cashRegister = cashRegisterStatus.cashRegister
      const paymentLinesForReceipt = buildClosingPaymentLines(actualAmount, paymentMethods, paymentAmounts)
      
      // Preparar array de paymentAmounts
      const paymentAmountsArray = paymentMethods
        .filter(method => {
          const value = paymentAmounts[method.payment_receipt_method_id]
          return value !== undefined && value !== null && value !== ''
        })
        .map(method => {
          const numericValue = parseMoneyBR(paymentAmounts[method.payment_receipt_method_id])
          return {
            payment_receipt_method_id: method.payment_receipt_method_id,
            operator_informed_amount: numericValue > 0 ? numericValue : null
          }
        })

      const response = await cashRegisterService.close(
        cashRegister.id,
        amount,
        paymentAmountsArray
      )

      if (response.success) {
        const closedData = response.data || {}
        const sessionSummary = closedData.sessionSummary || {}

        try {
          await printClosingReceipt({
            operatorName: user?.name || closedData.opened_by_name || closedData.closed_by_name,
            cashRegisterId: cashRegister.id,
            openingDate: cashRegister.opening_date || closedData.opening_date,
            closingDate: closedData.closing_date || new Date().toISOString(),
            paymentLines: paymentLinesForReceipt,
            totalSales: sessionSummary.totalSales ?? 0,
            totalItemsSold: sessionSummary.totalItemsSold ?? 0
          })
        } catch (printError) {
          console.error('Erro ao imprimir comprovante de fechamento:', printError)
        }

        showMessage('Sucesso', 'Caixa fechado com sucesso! O comprovante de fechamento foi enviado para impressão.', 'success', () => {
          setCloseCashRegisterModalOpen(false)
          setActualAmount('')
          setPaymentMethods([])
          setPaymentAmounts({})
          checkCashRegisterStatus()
        })
      } else {
        showMessage('Erro', response.message || 'Erro ao fechar caixa', 'danger')
      }
    } catch (error) {
      console.error('Erro ao fechar caixa:', error)
      showMessage('Erro', error.response?.data?.message || 'Erro ao fechar caixa', 'danger')
    } finally {
      setClosingCashRegister(false)
    }
  }



  // Calcular totais (antes dos efeitos que dependem de totals)
  const calculateTotals = () => {
    const totalItems = selectedProducts.length
    const totalAmount = selectedProducts.reduce((sum, p) => sum + p.totalPrice, 0)
    const totalDiscount = 0 // TODO: Implementar desconto
    const totalSurcharge = 0 // TODO: Implementar acréscimo
    const finalAmount = totalAmount - totalDiscount + totalSurcharge

    return {
      totalItems,
      totalAmount,
      totalDiscount,
      totalSurcharge,
      finalAmount
    }
  }

  const totals = calculateTotals()

  // Preencher valor restante ao entrar na etapa de pagamento
  useEffect(() => {
    if (step === 'payment' && appliedPayments.length === 0 && totals.finalAmount > 0 && !onlineOrderPaid) {
      setCurrentAmountInput(formatMoneyInputBR(totals.finalAmount))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  // Função para agrupar métodos hierarquicamente
  const groupPaymentMethodsHierarchically = (methods) => {
    if (!methods || methods.length === 0) return null

    const hierarchy = {
      DINHEIRO: [],
      CREDIÁRIO: [],
      'CRÉDITO_BÔNUS': [],
      TEF_PAGSEGURO: [],
      PIX_QRCODE: [],
      PIX_CNPJ: [],
      DÉBITO: {},
      'CRÉDITO_À_VISTA': {},
      'CRÉDITO_PARCELADO': {}
    }

    methods.forEach(method => {
      // Determinar payment_type
      let paymentType = method.payment_type
      
      // Normalizar payment_type
      if (!paymentType) {
        if (method.provider === 'DINHEIRO') {
          paymentType = 'DINHEIRO'
        } else if (method.provider === 'CREDIÁRIO' || method.provider === 'CREDIARIO' || isCrediarioMethod(method)) {
          paymentType = 'CREDIÁRIO'
        } else if (isCustomerCreditMethod(method)) {
          paymentType = 'CRÉDITO_BÔNUS'
        } else if (isTefPagseguroMethod(method)) {
          paymentType = 'TEF_PAGSEGURO'
        } else {
          return
        }
      }

      // Normalizar tipos de crédito
      if (paymentType === 'CRÉDITO' || paymentType === 'CREDITO' || paymentType === 'CRÉDITO_À_VISTA' || paymentType === 'CRÉDITO_PARCELADO') {
        if (method.installments && method.installments > 1) {
          paymentType = 'CRÉDITO_PARCELADO'
        } else {
          paymentType = 'CRÉDITO_À_VISTA'
        }
      }

      // Agrupar por tipo
      if (paymentType === 'CREDIÁRIO' || paymentType === 'CREDIARIO') {
        hierarchy.CREDIÁRIO.push(method)
      } else if (paymentType === 'CRÉDITO_BÔNUS' || paymentType === 'CREDITO_BONUS' || paymentType === 'CRÉDITO_BONUS') {
        hierarchy['CRÉDITO_BÔNUS'].push(method)
      } else if (paymentType === 'TEF_PAGSEGURO') {
        hierarchy.TEF_PAGSEGURO.push(method)
      } else if (paymentType === 'DINHEIRO') {
        hierarchy.DINHEIRO.push(method)
      } else if (paymentType === 'PIX_QRCODE') {
        hierarchy.PIX_QRCODE.push(method)
      } else if (paymentType === 'PIX_CNPJ') {
        hierarchy.PIX_CNPJ.push(method)
      } else if (paymentType === 'DÉBITO' && method.card_brand) {
        if (!hierarchy.DÉBITO[method.card_brand]) {
          hierarchy.DÉBITO[method.card_brand] = []
        }
        hierarchy.DÉBITO[method.card_brand].push(method)
      } else if (paymentType === 'CRÉDITO_À_VISTA' && method.card_brand) {
        if (!hierarchy['CRÉDITO_À_VISTA'][method.card_brand]) {
          hierarchy['CRÉDITO_À_VISTA'][method.card_brand] = []
        }
        hierarchy['CRÉDITO_À_VISTA'][method.card_brand].push(method)
      } else if (paymentType === 'CRÉDITO_PARCELADO' && method.card_brand && method.installments) {
        if (!hierarchy['CRÉDITO_PARCELADO'][method.card_brand]) {
          hierarchy['CRÉDITO_PARCELADO'][method.card_brand] = {}
        }
        const installmentsKey = `${method.installments}x`
        if (!hierarchy['CRÉDITO_PARCELADO'][method.card_brand][installmentsKey]) {
          hierarchy['CRÉDITO_PARCELADO'][method.card_brand][installmentsKey] = []
        }
        hierarchy['CRÉDITO_PARCELADO'][method.card_brand][installmentsKey].push(method)
      }
    })

    // Ordenar métodos dentro de cada grupo
    hierarchy.CREDIÁRIO.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    hierarchy.TEF_PAGSEGURO.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    hierarchy.DINHEIRO.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    hierarchy.PIX_QRCODE.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    hierarchy.PIX_CNPJ.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    
    Object.keys(hierarchy.DÉBITO).forEach(brand => {
      hierarchy.DÉBITO[brand].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    })
    
    Object.keys(hierarchy['CRÉDITO_À_VISTA']).forEach(brand => {
      hierarchy['CRÉDITO_À_VISTA'][brand].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    })
    
    Object.keys(hierarchy['CRÉDITO_PARCELADO']).forEach(brand => {
      Object.keys(hierarchy['CRÉDITO_PARCELADO'][brand]).forEach(installments => {
        hierarchy['CRÉDITO_PARCELADO'][brand][installments].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      })
    })

    return hierarchy
  }

  const loadSystemPaymentMethods = async ({ setDefaults = true } = {}) => {
    setLoadingSystemPaymentMethods(true)
    try {
      // Carregar lista plana de métodos
      const response = await paymentReceiptMethodService.getForSales({ active: true })
      
      if (response.success) {
        const methods = response.data || []
        setSystemPaymentMethods(methods)
        
        // Agrupar hierarquicamente no frontend
        const hierarchy = groupPaymentMethodsHierarchically(methods)
        setPaymentMethodsHierarchy(hierarchy)

        // Não sobrescrever seleção ao recarregar (ex.: pré-pago online já mapeado)
        if (!setDefaults) {
          return methods
        }
        
        // Selecionar Dinheiro por padrão se existir
        if (hierarchy?.DINHEIRO?.length > 0) {
          setSelectedPaymentType('DINHEIRO')
          setSelectedMethodId(hierarchy.DINHEIRO[0].id)
          setSelectedBrand(null)
        } else if (hierarchy?.PIX_QRCODE?.length > 0) {
          setSelectedPaymentType('PIX_QRCODE')
          setSelectedMethodId(hierarchy.PIX_QRCODE[0].id)
          setSelectedBrand(null)
        } else if (hierarchy?.PIX_CNPJ?.length > 0) {
          setSelectedPaymentType('PIX_CNPJ')
          setSelectedMethodId(hierarchy.PIX_CNPJ[0].id)
          setSelectedBrand(null)
        } else if (methods.length > 0) {
          // Fallback: selecionar primeiro método disponível
          const firstMethod = methods[0]
          if (firstMethod.payment_type === 'DINHEIRO' || firstMethod.provider === 'DINHEIRO') {
            setSelectedPaymentType('DINHEIRO')
            setSelectedBrand(null)
          } else if (firstMethod.payment_type === 'PIX_QRCODE') {
            setSelectedPaymentType('PIX_QRCODE')
            setSelectedBrand(null)
          } else if (firstMethod.payment_type === 'PIX_CNPJ') {
            setSelectedPaymentType('PIX_CNPJ')
            setSelectedBrand(null)
          } else if (firstMethod.payment_type === 'DÉBITO') {
            setSelectedPaymentType('DÉBITO')
            setSelectedBrand(firstMethod.card_brand || 'MASTER')
          } else if (firstMethod.payment_type === 'CRÉDITO' || firstMethod.payment_type === 'CRÉDITO_À_VISTA') {
            setSelectedPaymentType('CRÉDITO_À_VISTA')
            setSelectedBrand(firstMethod.card_brand || 'MASTER')
          } else if (firstMethod.payment_type === 'CRÉDITO_PARCELADO') {
            setSelectedPaymentType('CRÉDITO_PARCELADO')
            setSelectedBrand(firstMethod.card_brand || 'MASTER')
            setCardInstallments(firstMethod.installments || 2)
          }
          setSelectedMethodId(firstMethod.id)
        }
        return methods
      }
      return []
    } catch (error) {
      console.error('Erro ao carregar formas de recebimento:', error)
      showMessage('Erro', 'Erro ao carregar formas de recebimento', 'danger')
      return []
    } finally {
      setLoadingSystemPaymentMethods(false)
    }
  }

  // Obter ícone da forma de pagamento
  const getPaymentIcon = (method) => {
    const nameLower = (method.name || '').toLowerCase()
    if (nameLower.includes('dinheiro') || nameLower.includes('cash')) {
      return Banknote
    }
    if (nameLower.includes('tef') || nameLower.includes('pagseguro') || nameLower.includes('pagbank')) {
      return CreditCard
    }
    if (nameLower.includes('cartão') || nameLower.includes('card') || nameLower.includes('credito') || nameLower.includes('debito')) {
      return CreditCard
    }
    if (nameLower.includes('pix')) {
      return Smartphone
    }
    if (nameLower.includes('fatura') || nameLower.includes('billing')) {
      return FileText
    }
    return Wallet
  }

  // Verificar se método é dinheiro
  const isCashMethod = (method) => {
    return method.payment_type === 'DINHEIRO' || method.provider === 'DINHEIRO' || (method.name || '').toLowerCase().includes('dinheiro')
  }

  const isTefPagseguroMethod = (method) => {
    if (!method) return false
    const paymentType = String(method.payment_type || '').toUpperCase()
    const provider = String(method.provider || '').toUpperCase()
    const name = String(method.name || '').toLowerCase()
    return paymentType === 'TEF_PAGSEGURO' ||
      (provider === 'PAGSEGURO' && name.includes('tef')) ||
      name.includes('tef pagseguro')
  }

  const isPagseguroMachineMethod = (method) => {
    if (!method) return false
    if (isTefPagseguroMethod(method)) return true
    const provider = String(method.provider || '').toUpperCase()
    const name = String(method.name || '').toLowerCase()
    return provider === 'PAGSEGURO' ||
      name.includes('pagseguro') ||
      name.includes('pagbank') ||
      method.requires_confirmation === true
  }

  const isCardPaymentType = (paymentType) => (
    paymentType === 'DÉBITO' ||
    paymentType === 'CRÉDITO_À_VISTA' ||
    paymentType === 'CRÉDITO_PARCELADO'
  )

  const syncBrandWithPaymentType = (paymentType) => {
    setSelectedBrand(isCardPaymentType(paymentType) ? 'MASTER' : null)
  }

  // Buscar método de recebimento baseado em tipo, bandeira e parcelas
  const findPaymentMethod = (paymentType, cardBrand = null, installments = null) => {
    if (!paymentType) return null
    
    if (paymentType === 'DINHEIRO') {
      // Buscar método de dinheiro
      return systemPaymentMethods.find(m => 
        m.payment_type === 'DINHEIRO' || isCashMethod(m)
      )
    }

    if (paymentType === 'TEF_PAGSEGURO') {
      return systemPaymentMethods.find(isTefPagseguroMethod) ||
        paymentMethodsHierarchy?.TEF_PAGSEGURO?.[0] ||
        null
    }
    
    if (paymentType === 'PIX_QRCODE') {
      return systemPaymentMethods.find(m => 
        m.payment_type === 'PIX_QRCODE'
      )
    }
    
    if (paymentType === 'PIX_CNPJ') {
      return systemPaymentMethods.find(m => 
        m.payment_type === 'PIX_CNPJ'
      )
    }

    if (paymentType === 'CRÉDITO_BÔNUS') {
      return systemPaymentMethods.find(isCustomerCreditMethod)
    }
    
    if (paymentType === 'DÉBITO' && cardBrand) {
      // Buscar débito com bandeira específica
      return systemPaymentMethods.find(m => 
        m.payment_type === 'DÉBITO' && 
        m.card_brand === cardBrand &&
        (!m.installments || m.installments === 1)
      )
    }
    
    if (paymentType === 'CRÉDITO_À_VISTA' && cardBrand) {
      // Buscar crédito à vista com bandeira específica
      // Pode ser CRÉDITO_À_VISTA ou CRÉDITO sem parcelas
      return systemPaymentMethods.find(m => 
        (m.payment_type === 'CRÉDITO_À_VISTA' || m.payment_type === 'CRÉDITO' || m.payment_type === 'CREDITO') && 
        m.card_brand === cardBrand &&
        (!m.installments || m.installments === 1)
      )
    }
    
    if (paymentType === 'CRÉDITO_PARCELADO' && cardBrand && installments) {
      // Buscar crédito parcelado com bandeira e parcelas específicas
      // Pode ser CRÉDITO_PARCELADO ou CRÉDITO com parcelas
      return systemPaymentMethods.find(m => {
        const isCredit = m.payment_type === 'CRÉDITO_PARCELADO' || 
                        m.payment_type === 'CRÉDITO' || 
                        m.payment_type === 'CREDITO'
        return isCredit && 
               m.card_brand === cardBrand &&
               m.installments === parseInt(installments)
      })
    }
    
    return null
  }

  /** Mapeia pagamento já feito no site (PagBank) para forma de recebimento do ERP. */
  const resolveOnlinePrepaidReceiptMethod = (methodsList = systemPaymentMethods) => {
    const raw = String(onlinePaymentMethod || '').toUpperCase()
    const inst = Math.max(1, parseInt(onlineInstallments, 10) || 1)
    const brands = ['MASTER', 'VISA', 'ELO', 'AMEX', 'HIPERCARD', 'BANRISUL']
    const methods = Array.isArray(methodsList) ? methodsList : systemPaymentMethods

    const findInList = (paymentType, cardBrand = null, installments = null) => {
      if (paymentType === 'PIX_QRCODE') {
        return methods.find((m) => m.payment_type === 'PIX_QRCODE') ||
          methods.find((m) => String(m.name || '').toLowerCase().includes('pix'))
      }
      if (paymentType === 'DÉBITO' && cardBrand) {
        return methods.find((m) =>
          m.payment_type === 'DÉBITO' &&
          m.card_brand === cardBrand &&
          (!m.installments || Number(m.installments) === 1)
        )
      }
      if (paymentType === 'CRÉDITO_À_VISTA' && cardBrand) {
        return methods.find((m) =>
          (m.payment_type === 'CRÉDITO_À_VISTA' || m.payment_type === 'CRÉDITO' || m.payment_type === 'CREDITO') &&
          m.card_brand === cardBrand &&
          (!m.installments || Number(m.installments) === 1)
        )
      }
      if (paymentType === 'CRÉDITO_PARCELADO' && cardBrand && installments) {
        return methods.find((m) => {
          const isCredit = m.payment_type === 'CRÉDITO_PARCELADO' ||
            m.payment_type === 'CRÉDITO' ||
            m.payment_type === 'CREDITO'
          return isCredit &&
            m.card_brand === cardBrand &&
            Number(m.installments) === Number(installments)
        })
      }
      return null
    }

    const findCard = (paymentType, installments = null) => {
      for (const brand of brands) {
        const method = findInList(paymentType, brand, installments)
        if (method) {
          return { method, brand: method.card_brand || brand, installments: installments || 1 }
        }
      }
      const fallback = methods.find((m) => {
        if (paymentType === 'DÉBITO') {
          return m.payment_type === 'DÉBITO'
        }
        if (paymentType === 'CRÉDITO_À_VISTA') {
          return (
            (m.payment_type === 'CRÉDITO_À_VISTA' || m.payment_type === 'CRÉDITO' || m.payment_type === 'CREDITO') &&
            (!m.installments || Number(m.installments) === 1)
          )
        }
        if (paymentType === 'CRÉDITO_PARCELADO') {
          return (
            (m.payment_type === 'CRÉDITO_PARCELADO' || m.payment_type === 'CRÉDITO' || m.payment_type === 'CREDITO') &&
            Number(m.installments) === Number(installments)
          )
        }
        return false
      })
      if (!fallback) return null
      return {
        method: fallback,
        brand: fallback.card_brand || null,
        installments: installments || 1
      }
    }

    if (raw.includes('PIX')) {
      const method = findInList('PIX_QRCODE')
      return method ? { method, brand: null, installments: 1 } : null
    }

    if (raw.includes('DEBIT')) {
      return findCard('DÉBITO')
    }

    if (raw.includes('CREDIT') || raw.includes('CART') || raw.includes('CARD') || raw) {
      if (inst > 1) {
        return findCard('CRÉDITO_PARCELADO', inst) || findCard('CRÉDITO_À_VISTA')
      }
      return findCard('CRÉDITO_À_VISTA')
    }

    return findCard('CRÉDITO_À_VISTA')
  }

  /** Aplica pagamento pré-pago do site no caixa. Retorna true se vinculou. */
  const applyOnlinePrepaidPayment = (methodsList = systemPaymentMethods, { notify = true } = {}) => {
    const resolved = resolveOnlinePrepaidReceiptMethod(methodsList)
    const method = resolved?.method || null
    if (!method) return false

    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100
    const faceAmount = round2(totals.finalAmount)
    const inst = resolved?.installments || Math.max(1, parseInt(onlineInstallments, 10) || 1)
    const brand = resolved?.brand || null

    setSelectedPaymentType(method.payment_type || null)
    setSelectedMethodId(method.id)
    setSelectedBrand(brand)
    setCardInstallments(inst > 1 ? inst : 1)
    setAppliedPayments([
      {
        id: Math.random().toString(36).substr(2, 9),
        paymentReceiptMethodId: method.id,
        methodLabel: `${method.name || 'Pago online'} (já pago no site)`,
        paymentMethodName: method.name || 'Pago online',
        amount: faceAmount,
        installments: inst,
        brand,
        authorizationNumber: onlineOrderNumber || 'JA_PAGO_ONLINE',
        pagseguroTransactionId: onlinePagseguroTxId || null,
        isCash: false,
        isCustomerCredit: false,
        isTefPagseguro: false,
        requiresConfirmation: false,
        isOnlinePrepaid: true,
      },
    ])
    setCurrentAmountInput('')

    if (notify) {
      showMessage(
        'Já pago no site',
        'Pagamento preenchido automaticamente (valor face, sem taxa local). Confira e finalize — não cobre o cliente de novo.',
        'success'
      )
    }
    return true
  }

  // Prefetch das formas quando o caixa abre (evita 1º FINALIZAR sem métodos)
  useEffect(() => {
    if (cashRegisterStatus?.isOpen && systemPaymentMethods.length === 0 && !loadingSystemPaymentMethods) {
      loadSystemPaymentMethods({ setDefaults: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cashRegisterStatus?.isOpen])

  // Se entrar no pagamento sem métodos, carrega sem resetar seleção (pré-pago)
  useEffect(() => {
    if (step === 'payment' && systemPaymentMethods.length === 0) {
      loadSystemPaymentMethods({ setDefaults: !onlineOrderPaid })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  // Quando métodos chegam na etapa de pagamento, aplica pré-pago se ainda não aplicado
  useEffect(() => {
    if (step !== 'payment' || !onlineOrderPaid) return
    if (systemPaymentMethods.length === 0) return
    if (appliedPayments.some((p) => p.isOnlinePrepaid)) return

    const applied = applyOnlinePrepaidPayment(systemPaymentMethods, { notify: true })
    if (!applied) {
      showMessage(
        'Atenção',
        'Pedido já pago no site, mas não há forma de recebimento compatível (cartão/PIX) cadastrada. Cadastre em Formas de Recebimento.',
        'warning'
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, onlineOrderPaid, systemPaymentMethods, onlinePaymentMethod, onlineInstallments, totals.finalAmount])

  // Atualizar método selecionado quando bandeira ou parcelas mudarem (apenas quando necessário)
  useEffect(() => {
    if (!selectedPaymentType || systemPaymentMethods.length === 0) return

    let method = null

    if (selectedPaymentType === 'DINHEIRO') {
      method = findPaymentMethod('DINHEIRO')
    } else if (selectedPaymentType === 'TEF_PAGSEGURO') {
      method = findPaymentMethod('TEF_PAGSEGURO')
    } else if (selectedPaymentType === 'PIX_QRCODE') {
      method = findPaymentMethod('PIX_QRCODE')
    } else if (selectedPaymentType === 'PIX_CNPJ') {
      method = findPaymentMethod('PIX_CNPJ')
    } else if (selectedPaymentType === 'DÉBITO' && selectedBrand) {
      method = findPaymentMethod('DÉBITO', selectedBrand)
    } else if (selectedPaymentType === 'CRÉDITO_À_VISTA' && selectedBrand) {
      method = findPaymentMethod('CRÉDITO_À_VISTA', selectedBrand)
    } else if (selectedPaymentType === 'CRÉDITO_PARCELADO' && selectedBrand && cardInstallments) {
      method = findPaymentMethod('CRÉDITO_PARCELADO', selectedBrand, cardInstallments)
    } else if (selectedPaymentType === 'CREDIÁRIO' || selectedPaymentType === 'CREDIARIO') {
      method = paymentMethodsHierarchy?.CREDIÁRIO?.[0] || systemPaymentMethods.find(isCrediarioMethod)
    } else if (selectedPaymentType === 'CRÉDITO_BÔNUS') {
      method = findPaymentMethod('CRÉDITO_BÔNUS')
    }

    // Só atualizar se o método mudou
    if (method && method.id !== selectedMethodId) {
      setSelectedMethodId(method.id)
    } else if (!method && selectedMethodId) {
      setSelectedMethodId(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentType, selectedBrand, cardInstallments])

  // Mapear código de bandeira para nome
  const getBrandLabel = (brandCode) => {
    const brandMap = {
      'MASTER': 'Mastercard',
      'VISA': 'Visa',
      'ELO': 'Elo',
      'AMEX': 'Amex',
      'BANRISUL': 'Banrisul'
    }
    return brandMap[brandCode] || brandCode
  }

  const effectiveCustomerId = getEffectiveCustomerId(selectedCustomer, saleCustomerId)

  useEffect(() => {
    let cancelled = false

    const loadCustomerCredit = async () => {
      if (!effectiveCustomerId) {
        setCustomerCreditBalance(0)
        return
      }

      try {
        const response = await customerCreditService.getAvailableBalance(effectiveCustomerId)
        if (!cancelled && response.success) {
          const balance = response.data?.availableBalance ?? response.data?.available_balance ?? 0
          setCustomerCreditBalance(parseFloat(balance) || 0)
        }
      } catch (error) {
        console.error('Erro ao carregar crédito do cliente:', error)
        if (!cancelled) setCustomerCreditBalance(0)
      }
    }

    loadCustomerCredit()
    return () => { cancelled = true }
  }, [effectiveCustomerId])
  
  // Cálculos Financeiros de Pagamento
  const totalPaid = appliedPayments.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0)
  const creditUsedInPayments = appliedPayments
    .filter((p) => p.isCustomerCredit)
    .reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0)
  const customerCreditRemaining = Math.max(0, customerCreditBalance - creditUsedInPayments)
  const remainingBalance = Math.max(0, totals.finalAmount - totalPaid)
  const changeAmount = Math.max(0, totalPaid - totals.finalAmount)
  const isFullyPaid =
    totals.finalAmount > 0 &&
    totalPaid + MONEY_EPSILON >= totals.finalAmount

  const isAppliedPaymentCash = (payment) => {
    if (payment?.isCash === true) return true
    const method = systemPaymentMethods.find((m) => m.id === payment.paymentReceiptMethodId)
    return method ? isCashMethod(method) : false
  }

  const hasCashPayment = appliedPayments.some(isAppliedPaymentCash)
  const cashPaidTotal = appliedPayments
    .filter(isAppliedPaymentCash)
    .reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0)
  const cashReceivedPreview = parseMoneyBR(cashReceivedInput)
  const cashChangePreview = Math.max(0, cashReceivedPreview - cashPaidTotal)
  const cashChangeConfirmed =
    cashReceived != null ? Math.max(0, cashReceived - cashPaidTotal) : null

  const clearCashChangeState = () => {
    setCashChangeModalOpen(false)
    setCashReceivedInput('')
    setCashReceived(null)
  }

  // Formatar moeda
  const formatCurrency = (value) => {
    // Garantir que value é um número
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numValue)
  }

  const finishPaymentDisabledReason = loading
    ? 'Processando...'
    : appliedPayments.length === 0
      ? 'Clique no botão + ao lado do valor para registrar o pagamento'
      : remainingBalance > MONEY_EPSILON
        ? `Faltam ${formatCurrency(remainingBalance)} para quitar o total`
        : changeAmount > MONEY_EPSILON
          ? 'Valor pago excede o total — ajuste os pagamentos'
          : null

  // Carregar pré-vendas aguardando finalização
  const loadWaitingPresales = async () => {
    setLoadingWaiting(true)
    try {
      const response = await saleService.getAll({
        type: 'presale',
        status: 'reservation'
      })

      if (response.success) {
        setWaitingPresales(response.data || [])
        setWaitingModalOpen(true)
      }
    } catch (error) {
      console.error('Erro ao carregar pré-vendas aguardando:', error)
      showMessage('Erro', 'Erro ao carregar pré-vendas aguardando finalização', 'danger')
    } finally {
      setLoadingWaiting(false)
    }
  }

  // Selecionar pré-venda e carregar seus itens
  const handleSelectPresale = async (presale) => {
    try {
      setLoading(true)

      const response = await saleService.getById(presale.id)

      if (response.success && response.data) {
        const presaleData = response.data
        const items = presaleData.items || []

        if (items.length === 0) {
          showMessage(
            'Atenção',
            `A pré-venda #${presale.number} não possui itens cadastrados. Verifique se a pré-venda foi criada corretamente.`,
            'warning'
          )
          setWaitingModalOpen(false)
          setWaitingPresales([])
          return
        }

        const productsToAdd = items.map(item => ({
          id: item.product_id,
          name: item.product_name || `Produto #${item.product_id}`,
          sku: item.sku || '',
          barcode: item.barcode || '',
          image_url: item.image_url || null,
          price: parseFloat(item.unit_price || 0),
          quantity: parseFloat(item.quantity || 1),
          unitPrice: parseFloat(item.unit_price || 0),
          totalPrice: parseFloat(item.total_price || 0)
        }))

        setSelectedProducts(productsToAdd)
        setCurrentPresaleId(presaleData.id)
        setCurrentPresaleNumber(presaleData.number)
        setCurrentSellerId(presaleData.seller_id || null)
        setCountedQuantities({})
        setBarcodeInputs({})
        setBarcodeValidated({})

        const obs = String(presaleData.observation || '')
        const paidFlag =
          Boolean(presaleData.online_order_paid) ||
          obs.includes('JA_PAGO_ONLINE')
        const pendingLocal =
          Boolean(presaleData.online_order_id) &&
          !paidFlag &&
          (obs.includes('PAGAMENTO_PENDENTE_LOCAL') ||
            ['on_pickup', 'on_delivery'].includes(String(presaleData.online_payment_timing || '')))
        setOnlineOrderPaid(paidFlag)
        setOnlinePaymentPendingLocal(pendingLocal)
        setOnlineOrderNumber(presaleData.online_order_number || null)
        setOnlineOrderId(presaleData.online_order_id || null)
        setOnlinePaymentMethod(presaleData.online_payment_method || null)
        setOnlineInstallments(Math.max(1, parseInt(presaleData.online_installments, 10) || 1))
        setOnlinePagseguroTxId(presaleData.online_pagseguro_transaction_id || null)

        const { customer, customerId } = await resolveCustomerForCaixa(
          presaleData,
          presale,
          (id) => customerService.getById(id)
        )
        setSaleCustomerId(customerId)
        setSelectedCustomer(customer)

        if (presaleData.observation) {
          setObservations(presaleData.observation)
        }

        setWaitingModalOpen(false)
        setWaitingPresales([])

        setTimeout(() => {
          const payHint = paidFlag
            ? '\n\nPedido já pago no site — não cobre novamente no caixa.'
            : pendingLocal
              ? '\n\nPagamento pendente no local — registre a forma ao finalizar.'
              : ''
          showMessage(
            'Pré-venda carregada',
            `Pré-venda #${presale.number} carregada com sucesso! Revise os itens e clique em "Finalizar Venda".${payHint}`,
            'success'
          )
        }, 100)
      } else {
        showMessage('Erro', 'Não foi possível carregar os dados da pré-venda.', 'danger')
      }
    } catch (error) {
      console.error('Erro ao carregar pré-venda:', error)
      showMessage('Erro', error.response?.data?.message || 'Erro ao carregar pré-venda. Tente novamente.', 'danger')
    } finally {
      setLoading(false)
    }
  }

  // Atualizar quantidade contada
  const handleCountedQuantityChange = (productId, value) => {
    const numValue = parseFloat(value) || 0
    setCountedQuantities(prev => ({
      ...prev,
      [productId]: numValue
    }))
  }

  const normalizeProductCode = (value) => String(value || '').trim().toLowerCase()

  const matchesProductBarcode = (product, inputValue) => {
    const normalizedInput = normalizeProductCode(inputValue)
    if (!normalizedInput) return false
    const expectedCodes = [product.barcode, product.sku]
      .map(normalizeProductCode)
      .filter(Boolean)
    return expectedCodes.includes(normalizedInput)
  }

  const handleBarcodeInputChange = (product, value) => {
    const productId = product.id
    setBarcodeInputs(prev => ({ ...prev, [productId]: value }))
    const isValid = matchesProductBarcode(product, value)
    setBarcodeValidated(prev => ({ ...prev, [productId]: isValid }))
  }

  const focusCountInput = (productId) => {
    pendingCountFocusRef.current = productId
    const el = countInputRefs.current[productId]
    if (el && !el.disabled) {
      pendingCountFocusRef.current = null
      el.focus()
      el.select()
    }
  }

  const handleBarcodeKeyDown = (e, product) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    const value = e.currentTarget.value || barcodeInputs[product.id] || ''
    if (matchesProductBarcode(product, value)) {
      setBarcodeInputs(prev => ({ ...prev, [product.id]: value }))
      setBarcodeValidated(prev => ({ ...prev, [product.id]: true }))
      focusCountInput(product.id)
      return
    }
    showMessage(
      'Código inválido',
      `O código informado não corresponde ao produto ${product.name}.`,
      'warning'
    )
  }

  // Após validar o código (scanner), o input de contagem habilita — focar nele
  useEffect(() => {
    const productId = pendingCountFocusRef.current
    if (productId == null) return
    if (!barcodeValidated[productId]) return

    pendingCountFocusRef.current = null
    const el = countInputRefs.current[productId]
    if (!el || el.disabled) return
    el.focus()
    el.select()
  }, [barcodeValidated])

  // Verificar se todas as quantidades foram contadas corretamente (e códigos validados)
  const allQuantitiesCounted = () => {
    if (selectedProducts.length === 0) return false
    
    return selectedProducts.every(product => {
      const countedQty = parseFloat(countedQuantities[product.id]) || 0
      const expectedQty = parseFloat(product.quantity) || 0
      const barcodeOk = !!barcodeValidated[product.id]
      return barcodeOk && Math.abs(countedQty - expectedQty) < 0.001 && countedQty > 0
    })
  }

  // Handlers de Pagamento
  const handleAddPayment = () => {
    let amount = parseMoneyBR(currentAmountInput)
    if (amount <= 0) {
      amount = remainingBalance
    }
    if (amount <= 0) return

    if (!selectedPaymentType) {
      showMessage('Atenção', 'Selecione um tipo de pagamento', 'warning')
      return
    }

    // Buscar método baseado no tipo selecionado
    let method = null
    
    if (selectedPaymentType === 'DINHEIRO') {
      method = findPaymentMethod('DINHEIRO')
    } else if (selectedPaymentType === 'TEF_PAGSEGURO') {
      method = findPaymentMethod('TEF_PAGSEGURO')
    } else if (selectedPaymentType === 'DÉBITO') {
      if (!selectedBrand) {
        showMessage('Atenção', 'Selecione uma bandeira do cartão', 'warning')
        return
      }
      method = findPaymentMethod('DÉBITO', selectedBrand)
    } else if (selectedPaymentType === 'CRÉDITO_À_VISTA') {
      if (!selectedBrand) {
        showMessage('Atenção', 'Selecione uma bandeira do cartão', 'warning')
        return
      }
      method = findPaymentMethod('CRÉDITO_À_VISTA', selectedBrand)
    } else if (selectedPaymentType === 'CRÉDITO_PARCELADO') {
      if (!selectedBrand) {
        showMessage('Atenção', 'Selecione uma bandeira do cartão', 'warning')
        return
      }
      if (!cardInstallments || cardInstallments < 2) {
        showMessage('Atenção', 'Selecione o número de parcelas', 'warning')
        return
      }
      method = findPaymentMethod('CRÉDITO_PARCELADO', selectedBrand, cardInstallments)
    } else if (selectedPaymentType === 'PIX_QRCODE') {
      method = findPaymentMethod('PIX_QRCODE')
    } else if (selectedPaymentType === 'PIX_CNPJ') {
      method = findPaymentMethod('PIX_CNPJ')
    } else if (selectedPaymentType === 'CREDIÁRIO' || selectedPaymentType === 'CREDIARIO') {
      method = paymentMethodsHierarchy?.CREDIÁRIO?.[0] || systemPaymentMethods.find(isCrediarioMethod)
    } else if (selectedPaymentType === 'CRÉDITO_BÔNUS') {
      method = findPaymentMethod('CRÉDITO_BÔNUS')
    }

    if (!method) {
      showMessage('Erro', `Forma de recebimento não encontrada para ${selectedPaymentType}${selectedBrand ? ` ${getBrandLabel(selectedBrand)}` : ''}${cardInstallments > 1 ? ` ${cardInstallments}x` : ''}. Configure em Financeiro → Formas de Recebimento.`, 'danger')
      return
    }

    if (isCrediarioMethod(method)) {
      if (!effectiveCustomerId) {
        showMessage(
          'Atenção',
          'Crediário exige cliente na pré-venda. Vincule o cliente antes de finalizar a pré-venda e puxe a venda novamente no caixa.',
          'warning'
        )
        return
      }
      setPendingCrediarioPayment({ method, amount })
      setCrediarioModalOpen(true)
      return
    }

    if (isCustomerCreditMethod(method)) {
      if (!effectiveCustomerId) {
        showMessage('Atenção', 'Crédito/Bônus exige cliente cadastrado na venda.', 'warning')
        return
      }

      const creditAlreadyUsed = appliedPayments
        .filter((p) => p.isCustomerCredit)
        .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
      const creditAvailable = Math.max(0, customerCreditBalance - creditAlreadyUsed)

      if (creditAvailable <= 0) {
        showMessage('Atenção', 'Este cliente não possui saldo de crédito disponível.', 'warning')
        return
      }

      if (amount > creditAvailable + MONEY_EPSILON) {
        showMessage(
          'Atenção',
          `Valor excede o crédito disponível (${formatCurrency(creditAvailable)}).`,
          'warning'
        )
        return
      }
    }

    const newPayment = {
      id: Math.random().toString(36).substr(2, 9),
      paymentReceiptMethodId: method.id,
      methodLabel: method.name || '',
      paymentMethodName: method.name || '',
      amount: amount,
      installments: method.installments || (selectedPaymentType === 'CRÉDITO_PARCELADO' ? cardInstallments : undefined),
      brand: isCardPaymentType(selectedPaymentType)
        ? (method.card_brand || selectedBrand || undefined)
        : undefined,
      authorizationNumber: '',
      isCash: isCashMethod(method),
      isCustomerCredit: isCustomerCreditMethod(method),
      isTefPagseguro: isTefPagseguroMethod(method),
      requiresConfirmation: Boolean(method.requires_confirmation) || isPagseguroMachineMethod(method),
    }

    setAppliedPayments([...appliedPayments, newPayment])
    if (isCashMethod(method)) {
      setCashReceived(null)
      setCashReceivedInput('')
    }
    setCurrentAmountInput('')
    setCardInstallments(1)
    syncBrandWithPaymentType(selectedPaymentType)
  }

  const handleRemovePayment = (id) => {
    const next = appliedPayments.filter(p => p.id !== id)
    setAppliedPayments(next)
    const stillHasCash = next.some((p) => {
      if (p.isCash === true) return true
      const method = systemPaymentMethods.find((m) => m.id === p.paymentReceiptMethodId)
      return method ? isCashMethod(method) : false
    })
    if (!stillHasCash) {
      clearCashChangeState()
    } else {
      setCashReceived(null)
      setCashReceivedInput('')
    }
  }

  const handleConfirmCrediarioPayment = ({ paymentTermId = null, installments }) => {
    if (!pendingCrediarioPayment || !installments?.length) return
    const { method, amount } = pendingCrediarioPayment

    setAppliedPayments([
      ...appliedPayments,
      {
        id: Math.random().toString(36).substr(2, 9),
        paymentReceiptMethodId: method.id,
        methodLabel: method.name || 'Crediário',
        amount,
        isCrediario: true,
        paymentTermId: paymentTermId || null,
        installments,
        installmentCount: installments.length
      }
    ])

    setCrediarioModalOpen(false)
    setPendingCrediarioPayment(null)
    setCurrentAmountInput('')
    setCardInstallments(1)
    syncBrandWithPaymentType(selectedPaymentType)
  }

  const handleUpdatePaymentAuthorization = (paymentId, authorizationNumber) => {
    const cleaned = authorizationNumber.replace(/[^A-Z0-9\s\-_]/gi, '').slice(0, 100)
    setAppliedPayments(appliedPayments.map(p => 
      p.id === paymentId ? { ...p, authorizationNumber: cleaned } : p
    ))
  }

  // Finalizar venda - muda para etapa de pagamento
  const handleFinalizeSale = () => {
    if (selectedProducts.length === 0) {
      showMessage('Atenção', 'Adicione pelo menos um produto ao pedido', 'warning')
      return
    }

    // Validar códigos de barras e quantidades contadas
    if (!allQuantitiesCounted()) {
      const missingBarcodes = selectedProducts.filter(p => !barcodeValidated[p.id])
      const missingCounts = selectedProducts.filter(p => {
        if (!barcodeValidated[p.id]) return false
        return !countedQuantities[p.id] || countedQuantities[p.id] === 0
      })
      const incorrectCounts = selectedProducts.filter(p => {
        if (!barcodeValidated[p.id]) return false
        const countedQty = countedQuantities[p.id] || 0
        return countedQty > 0 && countedQty !== p.quantity
      })

      let message = 'Não é possível finalizar a venda:\n\n'
      
      if (missingBarcodes.length > 0) {
        message += `• ${missingBarcodes.length} produto(s) sem código de barras validado\n`
      }

      if (missingCounts.length > 0) {
        message += `• ${missingCounts.length} produto(s) ainda não foram contados\n`
      }
      
      if (incorrectCounts.length > 0) {
        message += `• ${incorrectCounts.length} produto(s) com quantidade incorreta\n`
      }
      
      message += '\nValide o código de barras e confirme as quantidades antes de finalizar.'
      
      showMessage('Atenção', message, 'warning')
      return
    }

    // Mudar para etapa de pagamento
    setStep('payment')

    if (onlineOrderPaid) {
      // Formas podem ainda não ter carregado: o useEffect aplica o pré-pago assim que estiverem prontas.
      if (systemPaymentMethods.length === 0) {
        loadSystemPaymentMethods({ setDefaults: false })
      } else {
        applyOnlinePrepaidPayment(systemPaymentMethods, { notify: true })
      }
      return
    }

    setCardInstallments(1)
    setSelectedBrand(null)
    setAppliedPayments([])
    setCurrentAmountInput(formatMoneyInputBR(totals.finalAmount))
  }

  const formatFinalizeError = (error) => {
    const apiMessage = error.response?.data?.message
    const apiErrors = error.response?.data?.errors
    if (Array.isArray(apiErrors) && apiErrors.length > 0) {
      const details = apiErrors.map((e) => e.msg || e.message).filter(Boolean).join('\n')
      return details || apiMessage || error.message
    }
    return apiMessage || error.message || 'Erro ao finalizar venda. Tente novamente.'
  }

  const formatCashReceivedInput = (rawValue) => {
    let value = String(rawValue || '').replace(/[^\d,]/g, '')
    const parts = value.split(',')
    let formatted = parts[0] || ''
    if (parts.length > 1) {
      formatted += ',' + parts.slice(1).join('').substring(0, 2)
    }
    return formatted
  }

  const requestFinishPayment = () => {
    if (loading) return
    if (!isFullyPaid) {
      showMessage('Atenção', 'O saldo deve ser totalmente quitado para finalizar.', 'warning')
      return
    }
    if (changeAmount > 0.009) {
      showMessage(
        'Atenção',
        `O total dos pagamentos (R$ ${totalPaid.toFixed(2)}) excede o valor da venda (R$ ${totals.finalAmount.toFixed(2)}). Ajuste os valores — troco não é aceito na finalização.`,
        'warning'
      )
      return
    }

    if (hasCashPayment && cashReceived == null) {
      setCashReceivedInput(formatMoneyInputBR(cashPaidTotal))
      setCashChangeModalOpen(true)
      return
    }

    handleFinishPayment()
  }

  const handleConfirmCashChange = () => {
    const received = parseMoneyBR(cashReceivedInput)
    if (received + MONEY_EPSILON < cashPaidTotal) {
      showMessage(
        'Atenção',
        `O valor recebido deve ser no mínimo ${formatCurrency(cashPaidTotal)} (total em dinheiro).`,
        'warning'
      )
      return
    }
    setCashReceived(received)
    setCashChangeModalOpen(false)
    handleFinishPayment({ cashReceivedOverride: received })
  }

  // Finalizar pagamento e venda (chamado quando clicar em Finalizar na etapa de pagamento)
  const handleFinishPayment = async (options = {}) => {
    if (loading) return // Prevenir múltiplas execuções
    const effectiveCashReceived = options.cashReceivedOverride ?? cashReceived
    
    if (!isFullyPaid) {
      showMessage('Atenção', 'O saldo deve ser totalmente quitado para finalizar.', 'warning')
      return
    }

    if (changeAmount > 0.009) {
      showMessage(
        'Atenção',
        `O total dos pagamentos (R$ ${totalPaid.toFixed(2)}) excede o valor da venda (R$ ${totals.finalAmount.toFixed(2)}). Ajuste os valores — troco não é aceito na finalização.`,
        'warning'
      )
      return
    }

    const needsMachineConfirmation = appliedPayments.some((payment) => {
      const method = systemPaymentMethods.find((m) => m.id === payment.paymentReceiptMethodId)
      const isMachine = payment.isTefPagseguro ||
        payment.requiresConfirmation ||
        isPagseguroMachineMethod(method)
      const hasAuth = Boolean(String(payment.authorizationNumber || '').trim())
      return isMachine && !hasAuth
    })

    if (needsMachineConfirmation) {
      setPendingPaymentData(appliedPayments.map((payment) => ({
        ...payment,
        paymentMethodName: payment.paymentMethodName || payment.methodLabel,
        name: payment.paymentMethodName || payment.methodLabel,
        requiresConfirmation: true,
        ...(payment.isCash && effectiveCashReceived != null
          ? { cashReceived: effectiveCashReceived, cashToDiscount: payment.amount }
          : {})
      })))
      setPaymentConfirmationModalOpen(true)
      return
    }
    
    const flowStartedAt = Date.now()
    setLoading(true)
    setLoadingStep('Validando caixa...')
    try {
      // Validar caixa aberto
      if (!cashRegisterStatus?.isOpen || !cashRegisterStatus?.cashRegister?.id) {
        showMessage('Erro', 'Caixa deve estar aberto para finalizar vendas', 'danger')
        setLoading(false)
        setLoadingStep('')
        return
      }

      // Criar venda em rascunho
      // Incluir referência à pré-venda na observation se houver
      let finalObservation = observations
      if (currentPresaleId && currentPresaleNumber) {
        const presaleRef = `Convertida da pré-venda #${currentPresaleNumber}`
        finalObservation = observations 
          ? `${presaleRef}\n${observations}` 
          : presaleRef
      }
      
      const saleData = {
        type: 'sale',
        customerId: effectiveCustomerId || null,
        saleDate: new Date().toISOString(),
        totalAmount: totals.totalAmount,
        discountAmount: totals.totalDiscount,
        taxAmount: totals.totalSurcharge,
        finalAmount: totals.finalAmount,
        status: 'draft', // Criar como rascunho primeiro
        observation: finalObservation,
        sellerId: currentSellerId || null,
        onlineOrderId: onlineOrderId || null
      }

      setLoadingStep('Criando venda...')
      console.log('[VendasPage] finalize: criando venda', { elapsedMs: Date.now() - flowStartedAt })
      const createResponse = await saleService.create(saleData)
      
      if (!createResponse.success) {
        throw new Error(createResponse.message || 'Erro ao criar venda')
      }

      const saleId = createResponse.data.id

      // Adicionar itens
      setLoadingStep(`Adicionando itens (0/${selectedProducts.length})...`)
      for (let i = 0; i < selectedProducts.length; i++) {
        const product = selectedProducts[i]
        setLoadingStep(`Adicionando itens (${i + 1}/${selectedProducts.length})...`)
        await saleService.addItem(saleId, {
          productId: product.id,
          quantity: product.quantity,
          unitPrice: product.unitPrice,
          totalPrice: product.totalPrice
        })
      }

      // Preparar pagamentos no formato esperado pelo backend
      const payments = appliedPayments.map(payment => ({
        paymentReceiptMethodId: payment.paymentReceiptMethodId,
        paymentTermId: payment.isCrediario ? payment.paymentTermId : null,
        installmentNumber: payment.isCrediario
          ? (payment.installments?.length || 1)
          : (payment.installments || 1),
        dueDate: new Date().toISOString(),
        amount: Math.round((Number(payment.amount) || 0) * 100) / 100,
        status: payment.isCrediario ? 'pending' : 'paid',
        observation: payment.isCrediario
          ? 'Crediário'
          : payment.isOnlinePrepaid
            ? 'Já pago no site (PagBank)'
            : null,
        authorizationNumber: payment.authorizationNumber || null,
        pagseguroTransactionId: payment.pagseguroTransactionId || null,
        installments: payment.isCrediario ? payment.installments : undefined,
        isCrediario: Boolean(payment.isCrediario),
        isOnlinePrepaid: Boolean(payment.isOnlinePrepaid)
      }))

      // Log dos dados que serão enviados para finalização
      console.log('🚀 [VendasPage] Finalizando venda:', {
        saleId,
        paymentsCount: payments.length,
        payments: payments,
        cashRegisterId: cashRegisterStatus.cashRegister.id,
        appliedPayments: appliedPayments
      })
      
      // Finalizar venda usando o novo endpoint (baixa estoque, cria pagamentos, etc.)
      setLoadingStep('Finalizando venda (estoque e pagamentos)...')
      console.log('[VendasPage] finalize: chamando /finalize', {
        saleId,
        elapsedMs: Date.now() - flowStartedAt,
        payments
      })
      const finalizeResponse = await saleService.finalizeSale(saleId, {
        payments: payments,
        cashRegisterId: cashRegisterStatus.cashRegister.id
      })

      console.log('📥 [VendasPage] Resposta da finalização:', {
        success: finalizeResponse.success,
        message: finalizeResponse.message,
        data: finalizeResponse.data,
        saleId: finalizeResponse.data?.id,
        payments: finalizeResponse.data?.payments,
        elapsedMs: Date.now() - flowStartedAt,
        pagseguroPayments: finalizeResponse.data?.payments?.filter(p => 
          p.pagseguro_transaction_id || p.payment_receipt_method?.name?.toLowerCase().includes('pagseguro')
        ) || [],
        paymentsDetails: finalizeResponse.data?.payments?.map(p => ({
          id: p.id,
          paymentReceiptMethodId: p.payment_receipt_method_id,
          methodName: p.payment_receipt_method?.name,
          pagseguroTransactionId: p.pagseguro_transaction_id,
          amount: p.amount,
          status: p.status
        })) || []
      })

      if (!finalizeResponse.success) {
        throw new Error(finalizeResponse.message || 'Erro ao finalizar venda')
      }

      // Salvar dados da venda para impressão (mas não abrir modal ainda)
      const productsToClear = [...selectedProducts]
      const customerToClear = selectedCustomer
      const nfePaymentsSnapshot = buildNfePaymentsFromApplied(appliedPayments)
      const customerIdForNfe = effectiveCustomerId || customerToClear?.id || null
      
      // Carregar venda completa com itens e pagamentos para impressão
      setLoadingStep('Carregando dados para impressão...')
      const saleDetailsResponse = await saleService.getById(saleId)
      
      if (saleDetailsResponse.success) {
        const saleForPrint = {
          ...saleDetailsResponse.data,
          items: saleDetailsResponse.data.items || productsToClear.map(p => ({
            product_id: p.id,
            product_name: p.name,
            quantity: p.quantity,
            unit_price: p.unitPrice,
            total_price: p.totalPrice
          })),
          customer_id: customerIdForNfe || saleDetailsResponse.data.customer_id,
          customer_name: customerToClear?.name || saleDetailsResponse.data.customer_name || null,
          customer_cpf_cnpj:
            customerToClear?.cpf_cnpj ||
            saleDetailsResponse.data.customer_cpf_cnpj ||
            null,
          nfePaymentsSnapshot
        }
        
        console.log('📄 Dados da venda para impressão:', saleForPrint)
        setCompletedSale(saleForPrint)
      } else {
        // Se não conseguir carregar detalhes, usar dados locais
        const saleForPrint = {
          ...finalizeResponse.data,
          id: saleId,
          items: productsToClear.map(p => ({
            product_id: p.id,
            product_name: p.name,
            quantity: p.quantity,
            unit_price: p.unitPrice,
            total_price: p.totalPrice
          })),
          customer_id: customerIdForNfe,
          customer_name: customerToClear?.name || null,
          customer_cpf_cnpj: customerToClear?.cpf_cnpj || null,
          nfePaymentsSnapshot
        }
        
        setCompletedSale(saleForPrint)
      }

      // Limpar formulário e mudar para tela de sucesso
      setSelectedProducts([])
      setObservations('')
      setSelectedCustomer(null)
      setSaleCustomerId(null)
      setCurrentPresaleId(null)
      setCurrentPresaleNumber(null)
      setCurrentSellerId(null)
      setOnlineOrderPaid(false)
      setOnlineOrderNumber(null)
      setOnlineOrderId(null)
      setOnlinePaymentMethod(null)
      setOnlineInstallments(1)
      setOnlinePagseguroTxId(null)
      setOnlinePaymentPendingLocal(false)
      setOnlineOrderInfoOpen(false)
      setAppliedPayments([])
      setCurrentAmountInput('')
      setSelectedPaymentType(null)
      setSelectedMethodId(null)
      setCardInstallments(1)
      setSelectedBrand('MASTER')
      clearCashChangeState()
      
      // Mudar para tela de sucesso
      setStep('success')
    } catch (error) {
      const elapsedMs = Date.now() - flowStartedAt
      console.error('[VendasPage] Erro ao finalizar venda:', {
        error,
        step: loadingStep,
        elapsedMs,
        status: error.response?.status,
        data: error.response?.data
      })
      const isTimeout = error.code === 'ECONNABORTED'
      showMessage(
        'Erro',
        isTimeout
          ? 'A finalização demorou demais (timeout). Verifique os logs do backend — pode ser integração PagSeguro ou travamento no banco.'
          : formatFinalizeError(error),
        'danger'
      )
    } finally {
      setLoading(false)
      setLoadingStep('')
    }
  }

  // Confirmar pagamento e finalizar venda (chamado pelo PaymentConfirmationModal)
  const handleConfirmPayment = async (paymentData) => {
    if (loading) return // Prevenir múltiplas execuções
    
    setLoading(true)
    try {
      // Validar caixa aberto
      if (!cashRegisterStatus?.isOpen || !cashRegisterStatus?.cashRegister?.id) {
        showMessage('Erro', 'Caixa deve estar aberto para finalizar vendas', 'danger')
        setLoading(false)
        return
      }

      // Validar paymentData
      if (!paymentData || !Array.isArray(paymentData) || paymentData.length === 0) {
        showMessage('Erro', 'Dados de pagamento inválidos', 'danger')
        setLoading(false)
        return
      }

      // Validar se o total dos pagamentos corresponde ao valor da venda
      const totalPayments = paymentData.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
      const difference = Math.abs(totalPayments - totals.finalAmount)
      
      if (difference > 0.01) {
        showMessage('Atenção', `O total dos pagamentos (R$ ${totalPayments.toFixed(2)}) deve ser igual ao valor da venda (R$ ${totals.finalAmount.toFixed(2)})`, 'warning')
        setLoading(false)
        return
      }

      // Criar venda em rascunho
      // Incluir referência à pré-venda na observation se houver
      let finalObservation = observations
      if (currentPresaleId && currentPresaleNumber) {
        const presaleRef = `Convertida da pré-venda #${currentPresaleNumber}`
        finalObservation = observations 
          ? `${presaleRef}\n${observations}` 
          : presaleRef
      }
      
      const saleData = {
        type: 'sale',
        customerId: effectiveCustomerId || null,
        saleDate: new Date().toISOString(),
        totalAmount: totals.totalAmount,
        discountAmount: totals.totalDiscount,
        taxAmount: totals.totalSurcharge,
        finalAmount: totals.finalAmount,
        status: 'draft', // Criar como rascunho primeiro
        observation: finalObservation,
        sellerId: currentSellerId || null,
        onlineOrderId: onlineOrderId || null
      }

      const createResponse = await saleService.create(saleData)
      
      if (!createResponse.success) {
        throw new Error(createResponse.message || 'Erro ao criar venda')
      }

      const saleId = createResponse.data.id

      // Adicionar itens
      for (const product of selectedProducts) {
        await saleService.addItem(saleId, {
          productId: product.id,
          quantity: product.quantity,
          unitPrice: product.unitPrice,
          totalPrice: product.totalPrice
        })
      }

      // Preparar pagamentos no formato esperado pelo backend
      const payments = paymentData.map(payment => ({
        paymentReceiptMethodId: payment.paymentReceiptMethodId || payment.paymentMethodId || null,
        paymentTermId: payment.paymentTermId || null,
        installmentNumber: payment.installmentNumber || 1,
        dueDate: payment.dueDate || new Date().toISOString(),
        amount: parseFloat(payment.amount) || 0,
        status: 'paid',
        observation: payment.observation || null,
        authorizationNumber: payment.authorizationNumber || null
      }))

      // Log dos dados que serão enviados para finalização
      console.log('🚀 [VendasPage] Finalizando venda via PaymentConfirmationModal:', {
        saleId,
        paymentsCount: payments.length,
        payments: payments,
        cashRegisterId: cashRegisterStatus.cashRegister.id,
        paymentData: paymentData
      })
      
      // Finalizar venda usando o novo endpoint (baixa estoque, cria pagamentos, etc.)
      const finalizeResponse = await saleService.finalizeSale(saleId, {
        payments: payments,
        cashRegisterId: cashRegisterStatus.cashRegister.id
      })

      console.log('📥 [VendasPage] Resposta da finalização:', {
        success: finalizeResponse.success,
        message: finalizeResponse.message,
        data: finalizeResponse.data,
        saleId: finalizeResponse.data?.id,
        payments: finalizeResponse.data?.payments
      })

      if (!finalizeResponse.success) {
        throw new Error(finalizeResponse.message || 'Erro ao finalizar venda')
      }

      // Fechar modal de confirmação de pagamento
      setPaymentConfirmationModalOpen(false)
      setPendingPaymentData(null)

      // Salvar dados da venda para impressão (mas não abrir modal ainda)
      const productsToClear = [...selectedProducts]
      const customerToClear = selectedCustomer
      const nfePaymentsSnapshot = buildNfePaymentsFromApplied(
        (paymentData || []).map((p) => ({
          paymentReceiptMethodId: p.paymentReceiptMethodId || p.paymentMethodId,
          amount: p.amount,
          isCrediario: p.isCrediario,
          installments: p.installments,
          paymentTermId: p.paymentTermId,
          status: p.status || 'paid'
        }))
      )
      const customerIdForNfe = effectiveCustomerId || customerToClear?.id || null
      
      // Carregar venda completa com itens e pagamentos para impressão
      const saleDetailsResponse = await saleService.getById(saleId)
      
      if (saleDetailsResponse.success) {
        const saleForPrint = {
          ...saleDetailsResponse.data,
          items: saleDetailsResponse.data.items || productsToClear.map(p => ({
            product_id: p.id,
            product_name: p.name,
            quantity: p.quantity,
            unit_price: p.unitPrice,
            total_price: p.totalPrice
          })),
          customer_id: customerIdForNfe || saleDetailsResponse.data.customer_id,
          customer_name: customerToClear?.name || saleDetailsResponse.data.customer_name || null,
          customer_cpf_cnpj:
            customerToClear?.cpf_cnpj ||
            saleDetailsResponse.data.customer_cpf_cnpj ||
            null,
          nfePaymentsSnapshot
        }
        
        console.log('📄 Dados da venda para impressão:', saleForPrint)
        setCompletedSale(saleForPrint)
      } else {
        // Se não conseguir carregar detalhes, usar dados locais
        const saleForPrint = {
          ...finalizeResponse.data,
          id: saleId,
          items: productsToClear.map(p => ({
            product_id: p.id,
            product_name: p.name,
            quantity: p.quantity,
            unit_price: p.unitPrice,
            total_price: p.totalPrice
          })),
          customer_id: customerIdForNfe,
          customer_name: customerToClear?.name || null,
          customer_cpf_cnpj: customerToClear?.cpf_cnpj || null,
          nfePaymentsSnapshot
        }
        
        setCompletedSale(saleForPrint)
      }

      // Limpar formulário e mudar para tela de sucesso
      setSelectedProducts([])
      setObservations('')
      setSelectedCustomer(null)
      setSaleCustomerId(null)
      setCurrentPresaleId(null)
      setCurrentPresaleNumber(null)
      setCurrentSellerId(null)
      setOnlineOrderPaid(false)
      setOnlineOrderNumber(null)
      setOnlineOrderId(null)
      setOnlinePaymentMethod(null)
      setOnlineInstallments(1)
      setOnlinePagseguroTxId(null)
      setOnlinePaymentPendingLocal(false)
      setOnlineOrderInfoOpen(false)
      setAppliedPayments([])
      setCurrentAmountInput('')
      setSelectedPaymentType(null)
      setSelectedMethodId(null)
      setCardInstallments(1)
      setSelectedBrand('MASTER')
      clearCashChangeState()
      
      // Mudar para tela de sucesso
      setStep('success')
    } catch (error) {
      console.error('Erro ao finalizar venda:', error)
      showMessage('Erro', error.response?.data?.message || error.message || 'Erro ao finalizar venda. Tente novamente.', 'danger')
    } finally {
      setLoading(false)
    }
  }

  // Resetar venda e voltar para etapa de contagem
  const resetSale = () => {
    setSelectedProducts([])
    setObservations('')
    setSelectedCustomer(null)
    setSaleCustomerId(null)
    setCurrentPresaleId(null)
    setCurrentPresaleNumber(null)
    setCurrentSellerId(null)
    setOnlineOrderPaid(false)
    setOnlineOrderNumber(null)
    setOnlineOrderId(null)
    setOnlinePaymentMethod(null)
    setOnlineInstallments(1)
    setOnlinePagseguroTxId(null)
    setOnlinePaymentPendingLocal(false)
    setOnlineOrderInfoOpen(false)
    setAppliedPayments([])
    setCurrentAmountInput('')
    setSelectedPaymentType(null)
    setSelectedMethodId(null)
    setCardInstallments(1)
    setSelectedBrand('MASTER')
    setCountedQuantities({})
    setBarcodeInputs({})
    setBarcodeValidated({})
    clearCashChangeState()
    setCompletedSale(null)
    setPrintModalOpen(false)
    setStep('counting')
  }

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false)
      }
      if (onlineOrderInfoRef.current && !onlineOrderInfoRef.current.contains(event.target)) {
        setOnlineOrderInfoOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])


  // Handler para abrir ERP em nova aba
  const handleAccessERP = () => {
    window.open('/erp', '_blank')
    setUserMenuOpen(false)
  }

  // Se ainda está verificando o caixa, mostrar loading
  if (checkingCashRegister) {
    return (
      <div className="vendas-page vendas-page-loading">
        <div className="vendas-loading-container">
          <div className="vendas-loading-spinner" />
          <Loader2 className="vendas-loading-icon" size={40} />
        </div>
        <p className="vendas-loading-text">
          Verificando status do caixa...
        </p>
      </div>
    )
  }

  return (
    <div className="vendas-page">
      {/* Modal de Abertura de Caixa */}
      <CashRegisterModal
        isOpen={cashRegisterModalOpen}
        onClose={handleExitFromModal}
        onOpen={handleCashRegisterOpened}
        required={true}
      />

      {/* MODAL: FECHAMENTO ÀS CEGAS */}
      {closeCashRegisterModalOpen && (
        <div className="vendas-modal-overlay" onClick={() => { if (!closingCashRegister) { setCloseCashRegisterModalOpen(false); setActualAmount(''); setPaymentMethods([]); setPaymentAmounts({}); } }}>
          <div className="vendas-modal-content vendas-modal-closing" onClick={(e) => e.stopPropagation()}>
            <div className="vendas-modal-header">
              <div className="vendas-modal-header-left">
                <div className="vendas-modal-icon vendas-modal-icon-danger">
                  <Calculator size={28} />
                </div>
                <div>
                  <h2 className="vendas-modal-title">Fechamento às Cegas</h2>
                  <p className="vendas-modal-subtitle">Declare os valores físicos em gaveta</p>
                </div>
              </div>
              <button onClick={() => { if (!closingCashRegister) { setCloseCashRegisterModalOpen(false); setActualAmount(''); setPaymentMethods([]); setPaymentAmounts({}); } }} className="vendas-modal-close">
                <X size={24} />
              </button>
            </div>

            <div className="vendas-modal-closing-grid">
              <div className="vendas-modal-closing-column">
                <div className="vendas-modal-closing-field">
                  <label className="vendas-modal-closing-label">
                    <span className="vendas-modal-closing-icon">💰</span> Dinheiro em Espécie
                  </label>
                  <div className="vendas-modal-closing-input-wrapper">
                    <span className="vendas-modal-closing-currency">R$</span>
                    <input 
                      type="text" 
                      value={actualAmount} 
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '')
                        if (value) {
                          value = (parseInt(value) / 100).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                        }
                        setActualAmount(value)
                      }}
                      className="vendas-modal-closing-input" 
                      placeholder="0,00" 
                    />
                  </div>
                </div>
                {paymentMethods.filter(m => m.payment_method_name?.toLowerCase().includes('débito') || m.payment_method_name?.toLowerCase().includes('debito')).map((method) => (
                  <div key={method.payment_receipt_method_id} className="vendas-modal-closing-field">
                    <label className="vendas-modal-closing-label">
                      <span className="vendas-modal-closing-icon">💳</span> {method.payment_method_name}
                    </label>
                    <div className="vendas-modal-closing-input-wrapper">
                      <span className="vendas-modal-closing-currency">R$</span>
                      <input 
                        type="text" 
                        value={paymentAmounts[method.payment_receipt_method_id] || ''} 
                        onChange={(e) => handlePaymentAmountInputChange(method.payment_receipt_method_id, e)}
                        className="vendas-modal-closing-input" 
                        placeholder="0,00" 
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="vendas-modal-closing-column">
                {paymentMethods.filter(m => !m.payment_method_name?.toLowerCase().includes('débito') && !m.payment_method_name?.toLowerCase().includes('debito')).map((method) => (
                  <div key={method.payment_receipt_method_id} className="vendas-modal-closing-field">
                    <label className="vendas-modal-closing-label">
                      <span className="vendas-modal-closing-icon">💳</span> {method.payment_method_name}
                    </label>
                    <div className="vendas-modal-closing-input-wrapper">
                      <span className="vendas-modal-closing-currency">R$</span>
                      <input 
                        type="text" 
                        value={paymentAmounts[method.payment_receipt_method_id] || ''} 
                        onChange={(e) => handlePaymentAmountInputChange(method.payment_receipt_method_id, e)}
                        className="vendas-modal-closing-input" 
                        placeholder="0,00" 
                      />
                    </div>
                  </div>
                ))}
                {loadingPaymentMethods && (
                  <div className="vendas-modal-closing-loading">
                    <Loader2 className="spinning" size={24} />
                  </div>
                )}
              </div>
            </div>

            <div className="vendas-modal-closing-actions">
              <button 
                onClick={() => { if (!closingCashRegister) { setCloseCashRegisterModalOpen(false); setActualAmount(''); setPaymentMethods([]); setPaymentAmounts({}); } }} 
                className="vendas-modal-closing-btn vendas-modal-closing-btn-cancel"
                disabled={closingCashRegister}
              >
                Cancelar Fechamento
              </button>
              <button 
                onClick={handleCloseCashRegister} 
                className="vendas-modal-closing-btn vendas-modal-closing-btn-confirm"
                disabled={closingCashRegister}
              >
                {closingCashRegister ? (
                  <>
                    <Loader2 className="spinning" size={18} />
                    Encerrando...
                  </>
                ) : (
                  <>
                    <Calculator size={18} />
                    Encerrar Turno e Gerar Relatório
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <PaymentConfirmationModal
        isOpen={paymentConfirmationModalOpen}
        onClose={() => {
          setPaymentConfirmationModalOpen(false)
          setPendingPaymentData(null)
        }}
        onConfirm={handleConfirmPayment}
        totalAmount={totals.finalAmount}
        paymentData={pendingPaymentData}
        saleData={{
          items: selectedProducts,
          customer: selectedCustomer,
          observations: observations
        }}
      />

      {/* Modal de Impressão — remonta a cada abertura */}
      {printModalOpen && completedSale && (
        <PrintModal
          key={`print-${completedSale.id}-${printModalInstance}`}
          isOpen
          saleData={completedSale}
          onClose={() => setPrintModalOpen(false)}
          onPrintComplete={() => setPrintModalOpen(false)}
        />
      )}

      {/* MODAL: SELEÇÃO DE TIPO DE PAGAMENTO */}
      {paymentTypeModalOpen && (
        <div className="vendas-modal-overlay" onClick={() => setPaymentTypeModalOpen(false)}>
          <div className="vendas-modal-content vendas-modal-payment-type" onClick={(e) => e.stopPropagation()}>
            <div className="vendas-modal-header">
              <div className="vendas-modal-header-left">
                <div className="vendas-modal-icon">
                  <Receipt size={28} />
                </div>
                <div>
                  <h2 className="vendas-modal-title">Tipo de Pagamento</h2>
                  <p className="vendas-modal-subtitle">Selecione a forma de recebimento</p>
                </div>
              </div>
              <button onClick={() => setPaymentTypeModalOpen(false)} className="vendas-modal-close">
                <X size={24} />
              </button>
            </div>
            <div className="vendas-modal-payment-type-body">
              {loadingSystemPaymentMethods ? (
                <div className="vendas-modal-waiting-loading">
                  <Loader2 className="spinning" size={32} />
                </div>
              ) : systemPaymentMethods.length === 0 ? (
                <div className="vendas-modal-waiting-empty">
                  <Wallet size={48} />
                  <p>Nenhuma forma de recebimento cadastrada</p>
                  <p style={{ fontSize: '0.75rem', marginTop: '0.5rem', opacity: 0.7 }}>
                    Configure em Financeiro → Formas de Recebimento
                  </p>
                </div>
              ) : (
                <div className="vendas-modal-payment-type-grid">
                  {/* Dinheiro */}
                  {paymentMethodsHierarchy?.DINHEIRO?.length > 0 && (
                    <button 
                      onClick={() => {
                        setSelectedPaymentType('DINHEIRO')
                        setSelectedMethodId(paymentMethodsHierarchy.DINHEIRO[0].id)
                        setSelectedBrand(null)
                        setCardInstallments(1)
                        setPaymentTypeModalOpen(false)
                      }}
                      className={`vendas-modal-payment-type-item ${selectedPaymentType === 'DINHEIRO' ? 'active' : ''}`}
                      style={{
                        '--method-color': '#10b981',
                        '--method-bg': '#d1fae5'
                      }}
                    >
                      <div className="vendas-modal-payment-type-icon" style={{ backgroundColor: '#d1fae5', color: '#10b981' }}>
                        <Banknote size={24} />
                      </div>
                      <div className="vendas-modal-payment-type-info">
                        <span className="vendas-modal-payment-type-label">Dinheiro</span>
                        <span className="vendas-modal-payment-type-count">{paymentMethodsHierarchy.DINHEIRO.length} método(s)</span>
                      </div>
                    </button>
                  )}

                  {/* Crediário */}
                  {(paymentMethodsHierarchy?.CREDIÁRIO?.length > 0 ||
                    systemPaymentMethods.some(isCrediarioMethod)) && (
                    <button
                      onClick={() => {
                        setSelectedPaymentType('CREDIÁRIO')
                        const method =
                          paymentMethodsHierarchy?.CREDIÁRIO?.[0] ||
                          systemPaymentMethods.find(isCrediarioMethod)
                        setSelectedMethodId(method?.id || null)
                        setSelectedBrand(null)
                        setCardInstallments(1)
                        setPaymentTypeModalOpen(false)
                      }}
                      className={`vendas-modal-payment-type-item ${selectedPaymentType === 'CREDIÁRIO' ? 'active' : ''}`}
                      style={{
                        '--method-color': '#d97706',
                        '--method-bg': '#fef3c7'
                      }}
                    >
                      <div className="vendas-modal-payment-type-icon" style={{ backgroundColor: '#fef3c7', color: '#d97706' }}>
                        <CalendarClock size={24} />
                      </div>
                      <div className="vendas-modal-payment-type-info">
                        <span className="vendas-modal-payment-type-label">Crediário</span>
                        <span className="vendas-modal-payment-type-count">Contas a receber</span>
                      </div>
                    </button>
                  )}

                  {/* Crédito / Bônus */}
                  {(paymentMethodsHierarchy?.['CRÉDITO_BÔNUS']?.length > 0 ||
                    systemPaymentMethods.some(isCustomerCreditMethod)) &&
                    effectiveCustomerId &&
                    customerCreditRemaining > 0 && (
                    <button
                      onClick={() => {
                        setSelectedPaymentType('CRÉDITO_BÔNUS')
                        const method =
                          paymentMethodsHierarchy?.['CRÉDITO_BÔNUS']?.[0] ||
                          systemPaymentMethods.find(isCustomerCreditMethod)
                        setSelectedMethodId(method?.id || null)
                        setSelectedBrand(null)
                        setCardInstallments(1)
                        setPaymentTypeModalOpen(false)
                      }}
                      className={`vendas-modal-payment-type-item ${selectedPaymentType === 'CRÉDITO_BÔNUS' ? 'active' : ''}`}
                      style={{
                        '--method-color': '#7c3aed',
                        '--method-bg': '#ede9fe'
                      }}
                    >
                      <div className="vendas-modal-payment-type-icon" style={{ backgroundColor: '#ede9fe', color: '#7c3aed' }}>
                        <Wallet size={24} />
                      </div>
                      <div className="vendas-modal-payment-type-info">
                        <span className="vendas-modal-payment-type-label">Crédito / Bônus</span>
                        <span className="vendas-modal-payment-type-count">
                          Disponível: {formatCurrency(customerCreditRemaining)}
                        </span>
                      </div>
                    </button>
                  )}

                  {/* TEF PAGSEGURO — Moderninha */}
                  {(paymentMethodsHierarchy?.TEF_PAGSEGURO?.length > 0 ||
                    systemPaymentMethods.some(isTefPagseguroMethod)) && (
                    <button
                      onClick={() => {
                        setSelectedPaymentType('TEF_PAGSEGURO')
                        const method =
                          paymentMethodsHierarchy?.TEF_PAGSEGURO?.[0] ||
                          systemPaymentMethods.find(isTefPagseguroMethod)
                        setSelectedMethodId(method?.id || null)
                        setSelectedBrand(null)
                        setCardInstallments(1)
                        setPaymentTypeModalOpen(false)
                      }}
                      className={`vendas-modal-payment-type-item ${selectedPaymentType === 'TEF_PAGSEGURO' ? 'active' : ''}`}
                      style={{
                        '--method-color': '#0f766e',
                        '--method-bg': '#ccfbf1'
                      }}
                    >
                      <div className="vendas-modal-payment-type-icon" style={{ backgroundColor: '#ccfbf1', color: '#0f766e' }}>
                        <CreditCard size={24} />
                      </div>
                      <div className="vendas-modal-payment-type-info">
                        <span className="vendas-modal-payment-type-label">TEF PAGSEGURO</span>
                        <span className="vendas-modal-payment-type-count">Moderninha Smart 2</span>
                      </div>
                    </button>
                  )}

                  {/* PIX QRCode */}
                  {paymentMethodsHierarchy?.PIX_QRCODE?.length > 0 && (
                    <button 
                      onClick={() => {
                        setSelectedPaymentType('PIX_QRCODE')
                        const method = findPaymentMethod('PIX_QRCODE')
                        setSelectedMethodId(method?.id || null)
                        setSelectedBrand(null)
                        setCardInstallments(1)
                        setPaymentTypeModalOpen(false)
                      }}
                      className={`vendas-modal-payment-type-item ${selectedPaymentType === 'PIX_QRCODE' ? 'active' : ''}`}
                      style={{
                        '--method-color': '#9333ea',
                        '--method-bg': '#f3e8ff'
                      }}
                    >
                      <div className="vendas-modal-payment-type-icon" style={{ backgroundColor: '#f3e8ff', color: '#9333ea' }}>
                        <Smartphone size={24} />
                      </div>
                      <div className="vendas-modal-payment-type-info">
                        <span className="vendas-modal-payment-type-label">PIX QRCode</span>
                        <span className="vendas-modal-payment-type-count">{paymentMethodsHierarchy.PIX_QRCODE.length} método(s)</span>
                      </div>
                    </button>
                  )}

                  {/* PIX CNPJ */}
                  {paymentMethodsHierarchy?.PIX_CNPJ?.length > 0 && (
                    <button 
                      onClick={() => {
                        setSelectedPaymentType('PIX_CNPJ')
                        const method = findPaymentMethod('PIX_CNPJ')
                        setSelectedMethodId(method?.id || null)
                        setSelectedBrand(null)
                        setCardInstallments(1)
                        setPaymentTypeModalOpen(false)
                      }}
                      className={`vendas-modal-payment-type-item ${selectedPaymentType === 'PIX_CNPJ' ? 'active' : ''}`}
                      style={{
                        '--method-color': '#9333ea',
                        '--method-bg': '#f3e8ff'
                      }}
                    >
                      <div className="vendas-modal-payment-type-icon" style={{ backgroundColor: '#f3e8ff', color: '#9333ea' }}>
                        <Smartphone size={24} />
                      </div>
                      <div className="vendas-modal-payment-type-info">
                        <span className="vendas-modal-payment-type-label">PIX CNPJ</span>
                        <span className="vendas-modal-payment-type-count">{paymentMethodsHierarchy.PIX_CNPJ.length} método(s)</span>
                      </div>
                    </button>
                  )}

                  {/* Débito */}
                  {paymentMethodsHierarchy?.DÉBITO && Object.keys(paymentMethodsHierarchy.DÉBITO).length > 0 && (
                    <button 
                      onClick={() => {
                        setSelectedPaymentType('DÉBITO')
                        setSelectedMethodId(null)
                        setCardInstallments(1)
                        setPaymentTypeModalOpen(false)
                      }}
                      className={`vendas-modal-payment-type-item ${selectedPaymentType === 'DÉBITO' ? 'active' : ''}`}
                      style={{
                        '--method-color': '#0ea5e9',
                        '--method-bg': '#e0f2fe'
                      }}
                    >
                      <div className="vendas-modal-payment-type-icon" style={{ backgroundColor: '#e0f2fe', color: '#0ea5e9' }}>
                        <CreditCard size={24} />
                      </div>
                      <div className="vendas-modal-payment-type-info">
                        <span className="vendas-modal-payment-type-label">Cartão de Débito</span>
                        <span className="vendas-modal-payment-type-count">
                          {Object.keys(paymentMethodsHierarchy.DÉBITO).length} bandeira(s)
                        </span>
                      </div>
                    </button>
                  )}

                  {/* Crédito à Vista */}
                  {paymentMethodsHierarchy?.['CRÉDITO_À_VISTA'] && Object.keys(paymentMethodsHierarchy['CRÉDITO_À_VISTA']).length > 0 && (
                    <button 
                      onClick={() => {
                        setSelectedPaymentType('CRÉDITO_À_VISTA')
                        setSelectedMethodId(null)
                        setCardInstallments(1)
                        setPaymentTypeModalOpen(false)
                      }}
                      className={`vendas-modal-payment-type-item ${selectedPaymentType === 'CRÉDITO_À_VISTA' ? 'active' : ''}`}
                      style={{
                        '--method-color': '#3b82f6',
                        '--method-bg': '#dbeafe'
                      }}
                    >
                      <div className="vendas-modal-payment-type-icon" style={{ backgroundColor: '#dbeafe', color: '#3b82f6' }}>
                        <CreditCard size={24} />
                      </div>
                      <div className="vendas-modal-payment-type-info">
                        <span className="vendas-modal-payment-type-label">Crédito à Vista</span>
                        <span className="vendas-modal-payment-type-count">
                          {Object.keys(paymentMethodsHierarchy['CRÉDITO_À_VISTA']).length} bandeira(s)
                        </span>
                      </div>
                    </button>
                  )}

                  {/* Crédito Parcelado */}
                  {paymentMethodsHierarchy?.['CRÉDITO_PARCELADO'] && Object.keys(paymentMethodsHierarchy['CRÉDITO_PARCELADO']).length > 0 && (
                    <button 
                      onClick={() => {
                        setSelectedPaymentType('CRÉDITO_PARCELADO')
                        setSelectedMethodId(null)
                        setCardInstallments(2)
                        setPaymentTypeModalOpen(false)
                      }}
                      className={`vendas-modal-payment-type-item ${selectedPaymentType === 'CRÉDITO_PARCELADO' ? 'active' : ''}`}
                      style={{
                        '--method-color': '#6366f1',
                        '--method-bg': '#e0e7ff'
                      }}
                    >
                      <div className="vendas-modal-payment-type-icon" style={{ backgroundColor: '#e0e7ff', color: '#6366f1' }}>
                        <CreditCard size={24} />
                      </div>
                      <div className="vendas-modal-payment-type-info">
                        <span className="vendas-modal-payment-type-label">Crédito Parcelado</span>
                        <span className="vendas-modal-payment-type-count">
                          {Object.keys(paymentMethodsHierarchy['CRÉDITO_PARCELADO']).length} bandeira(s)
                        </span>
                      </div>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL: VENDAS AGUARDANDO IMPORTAÇÃO */}
      {waitingModalOpen && (
        <div className="vendas-modal-overlay" onClick={() => { setWaitingModalOpen(false); setWaitingPresales([]); }}>
          <div className="vendas-modal-content vendas-modal-waiting" onClick={(e) => e.stopPropagation()}>
            <div className="vendas-modal-header">
              <div className="vendas-modal-header-left">
                <div className="vendas-modal-icon">
                  <Clock size={28} />
                </div>
                <div>
                  <h2 className="vendas-modal-title">Vendas em Espera</h2>
                  <p className="vendas-modal-subtitle">Pré-Vendas prontas para faturamento</p>
                </div>
              </div>
              <button onClick={() => { setWaitingModalOpen(false); setWaitingPresales([]); }} className="vendas-modal-close">
                <X size={24} />
              </button>
            </div>
            <div className="vendas-modal-waiting-body">
              {loadingWaiting ? (
                <div className="vendas-modal-waiting-loading">
                  <Loader2 className="spinning" size={32} />
                </div>
              ) : waitingPresales.length === 0 ? (
                <div className="vendas-modal-waiting-empty">
                  <Clock size={48} />
                  <p>Nenhuma pré-venda aguardando</p>
                </div>
              ) : (
                waitingPresales.map((presale) => (
                  <div key={presale.id} className="vendas-modal-waiting-item">
                    <div className="vendas-modal-waiting-item-left">
                      <div className="vendas-modal-waiting-time">
                        {new Date(presale.sale_date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <div>
                        <p className="vendas-modal-waiting-id">PV-{presale.number}</p>
                        <h4 className="vendas-modal-waiting-client">{presale.customer_name || 'Não informado'}</h4>
                        <p className="vendas-modal-waiting-details">
                          {new Date(presale.sale_date).toLocaleDateString('pt-BR')} • Total {formatCurrency(presale.final_amount)}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => handleSelectPresale(presale)} className="vendas-modal-waiting-btn">
                      Puxar Venda <ArrowUpRight size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div className="vendas-header">
        <div className="vendas-header-left">
          <div className="vendas-logo">
            <div className="vendas-logo-icon">W</div>
            <div>
              <h1 className="vendas-logo-title">Pró Médico <span className="vendas-logo-pos">Vendas</span></h1>
              <p className="vendas-logo-subtitle">Finalização de Vendas</p>
            </div>
          </div>
        </div>

        <div className="vendas-header-right">
          {(onlineOrderPaid || onlinePaymentPendingLocal) && selectedProducts.length > 0 && step !== 'success' ? (
            <div className="vendas-online-order-info" ref={onlineOrderInfoRef}>
              <button
                type="button"
                onClick={() => setOnlineOrderInfoOpen((open) => !open)}
                className={`vendas-header-btn vendas-header-btn-online-order${onlineOrderPaid ? ' is-paid' : ' is-local'}${onlineOrderInfoOpen ? ' active' : ''}`}
                title={onlineOrderPaid ? 'Pedido já pago no site' : 'Pagamento no local'}
                aria-expanded={onlineOrderInfoOpen}
                aria-haspopup="true"
              >
                {onlineOrderPaid ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
                <span className="vendas-header-badge vendas-header-badge-online" />
              </button>
              {onlineOrderInfoOpen && (
                <div className={`vendas-online-order-dropdown${onlineOrderPaid ? ' is-paid' : ' is-local'}`}>
                  <div className="vendas-online-order-dropdown-header">
                    <Package size={16} />
                    <span>Pedido online</span>
                  </div>
                  {onlineOrderNumber ? (
                    <p className="vendas-online-order-number">{onlineOrderNumber}</p>
                  ) : null}
                  {onlineOrderPaid ? (
                    <ul className="vendas-online-order-list">
                      <li>Já pago no site — não cobrar novamente</li>
                      <li>Após a conferência, finalize e emita a NFC-e</li>
                    </ul>
                  ) : (
                    <ul className="vendas-online-order-list">
                      <li>Pagamento pendente no local</li>
                      <li>Registre a forma de pagamento no caixa</li>
                      <li>Emita o cupom somente depois do pagamento</li>
                    </ul>
                  )}
                </div>
              )}
            </div>
          ) : null}
          <button
            onClick={() => loadWaitingPresales()}
            className="vendas-header-btn vendas-header-btn-history"
            title="Pré-vendas aguardando finalização"
          >
            <History size={18} />
            {waitingPresales.length > 0 && (
              <span className="vendas-header-badge" />
            )}
          </button>
          <div className="vendas-header-divider" />
          <div className="vendas-user-menu-container" ref={userMenuRef}>
            <button 
              onClick={() => setUserMenuOpen(!userMenuOpen)} 
              className={`vendas-user-menu-btn ${userMenuOpen ? 'active' : ''}`}
            >
              <div className="vendas-user-avatar">
                <img src={user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || user?.email || 'U')}&background=1e3a8a&color=fff`} alt="" />
              </div>
              <span className="vendas-user-name">{user?.name?.split(' ')[0] || 'Usuário'}</span>
              <ChevronDown size={12} className={`vendas-user-chevron ${userMenuOpen ? 'open' : ''}`} />
            </button>

            {/* USER DROPDOWN */}
            {userMenuOpen && (
              <div className="vendas-user-dropdown">
                <div className="vendas-user-dropdown-header">
                  <p className="vendas-user-dropdown-label">Logado como</p>
                  <p className="vendas-user-dropdown-name">{user?.name || user?.email}</p>
                </div>
                <div className="vendas-user-dropdown-body">
                  <button 
                    onClick={() => { setUserMenuOpen(false); handleAccessERP(); }}
                    className="vendas-user-dropdown-item"
                  >
                    <Database size={16} />
                    <span>Acessar ERP</span>
                  </button>
                  <button 
                    onClick={() => { setUserMenuOpen(false); setCloseCashRegisterModalOpen(true); }}
                    className="vendas-user-dropdown-item vendas-user-dropdown-item-danger"
                  >
                    <Lock size={16} />
                    <span>Fechar Caixa (F12)</span>
                  </button>
                  <div className="vendas-user-dropdown-divider" />
                  <button 
                    onClick={() => { setUserMenuOpen(false); logout(); navigate('/'); }}
                    className="vendas-user-dropdown-item vendas-user-dropdown-item-logout"
                  >
                    <LogOut size={16} />
                    <span>Sair do Terminal</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CONTEÚDO PRINCIPAL */}
      <div className="vendas-main-content">
        {step === 'counting' ? (
          <>
            <div className="vendas-products-section">
              <div className="vendas-products-container">
                <div className="vendas-products-header">
                  <div className="vendas-products-header-left">
                    <div className="vendas-products-icon">
                      <Box size={16} />
                    </div>
                    <span className="vendas-products-title">Conferência Cega (MEET)</span>
                  </div>
                  <span className="vendas-products-badge">
                    {selectedProducts.filter(p => {
                      const countedQty = countedQuantities[p.id] || 0
                      return !!barcodeValidated[p.id] && countedQty === p.quantity && countedQty > 0
                    }).length} / {selectedProducts.length} Conferidos
                  </span>
                </div>
                <div className="vendas-products-body">
                  {selectedProducts.length === 0 ? (
                    <div className="vendas-products-empty">
                      <div className="vendas-products-empty-icon">
                        <Box size={40} />
                      </div>
                      <p className="vendas-products-empty-title">Aguardando Importação</p>
                      <p className="vendas-products-empty-subtitle">Clique no relógio para buscar vendas da pré-venda</p>
                    </div>
                  ) : (
                    selectedProducts.map((product) => {
                      const countedQty = countedQuantities[product.id] || 0
                      const isBarcodeOk = !!barcodeValidated[product.id]
                      const isCorrect = isBarcodeOk && countedQty === product.quantity && countedQty > 0
                      
                      return (
                        <div key={product.id} className={`vendas-product-item ${isCorrect ? 'verified' : ''}`}>
                          {isCorrect && (
                            <div className="vendas-product-item-status" title="Conferido">
                              <CheckCircle2 size={12} />
                            </div>
                          )}
                          <div className="vendas-product-item-left">
                            <div className="vendas-product-item-image">
                              {product.image_url ? (
                                <img src={product.image_url} alt={product.name} />
                              ) : (
                                <Box size={24} />
                              )}
                            </div>
                            <div className="vendas-product-item-info">
                              <p className="vendas-product-item-sku">{product.sku || `ID: ${product.id}`}</p>
                              <h4 className="vendas-product-item-name">{product.name}</h4>
                              <p className="vendas-product-item-price">Unit: {formatCurrency(product.unitPrice)} • Total: {formatCurrency(product.totalPrice)}</p>
                            </div>
                          </div>
                          <div className="vendas-product-item-barcode">
                            <span className="vendas-product-item-label">
                              <ScanBarcode size={10} /> Cód. Barras
                            </span>
                            <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              disabled={isCorrect}
                              value={barcodeInputs[product.id] || ''}
                              onChange={(e) => handleBarcodeInputChange(product, e.target.value)}
                              onKeyDown={(e) => handleBarcodeKeyDown(e, product)}
                              className={`vendas-product-item-barcode-input ${isBarcodeOk ? 'verified' : ''}`}
                              placeholder="Scaneie ou digite"
                            />
                          </div>
                          <div className="vendas-product-item-right">
                            <span className="vendas-product-item-label">Contagem</span>
                            <input 
                              type="number" 
                              disabled={!isBarcodeOk || isCorrect}
                              value={countedQty || ''}
                              onChange={(e) => handleCountedQuantityChange(product.id, e.target.value)}
                              ref={(el) => {
                                if (el) countInputRefs.current[product.id] = el
                                else delete countInputRefs.current[product.id]
                              }}
                              className={`vendas-product-item-input ${isCorrect ? 'verified' : ''}`}
                              placeholder="0" 
                            />
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            </div>

            {/* LADO DIREITO: RESUMO FINANCEIRO */}
            <div className="vendas-summary-panel">
              <div className="vendas-summary-gradient" />

              <div className="vendas-summary-header">
                <h3 className="vendas-summary-header-title">
                  <Calculator size={16} /> Checkout
                </h3>
              </div>

              <div className="vendas-summary-content">
                <div className="vendas-summary-row">
                  <span className="vendas-summary-label">Total Bruto</span>
                  <span className="vendas-summary-value">{formatCurrency(totals.finalAmount)}</span>
                </div>

                <div className="vendas-summary-info">
                  <Info size={16} />
                  <p>
                    {selectedProducts.length === 0 
                      ? 'Aguardando carregamento de pré-venda para iniciar conferência.' 
                      : !allQuantitiesCounted() 
                        ? 'Valide o código de barras, conte os produtos e confirme as quantidades antes de finalizar.' 
                        : 'Todas as quantidades conferidas. Pronto para finalizar a venda.'}
                  </p>
                </div>
              </div>

              <div className="vendas-summary-footer">
                <div className="vendas-summary-total-card">
                  <div className="vendas-summary-total-inner">
                    <p className="vendas-summary-total-label">Total a Pagar</p>
                    <p className="vendas-summary-total-value">{formatCurrency(totals.finalAmount)}</p>
                  </div>
                </div>

                <div className="vendas-summary-actions">
                  <button 
                    onClick={() => {
                      setSelectedProducts([])
                      setCountedQuantities({})
                      setBarcodeInputs({})
                      setBarcodeValidated({})
                      setSelectedCustomer(null)
                      setSaleCustomerId(null)
                      setObservations('')
                      setCurrentPresaleId(null)
                      setCurrentPresaleNumber(null)
                      setCurrentSellerId(null)
                      setOnlineOrderPaid(false)
                      setOnlineOrderNumber(null)
                      setOnlineOrderId(null)
                      setOnlinePaymentMethod(null)
                      setOnlineInstallments(1)
                      setOnlinePagseguroTxId(null)
                      setOnlinePaymentPendingLocal(false)
                      setOnlineOrderInfoOpen(false)
                    }}
                    className="vendas-summary-btn vendas-summary-btn-cancel"
                  >
                    <X size={16} /> Cancelar
                  </button>
                  <button 
                    onClick={handleFinalizeSale}
                    disabled={loading || selectedProducts.length === 0 || !allQuantitiesCounted()} 
                    className="vendas-summary-btn vendas-summary-btn-finalize"
                  >
                    <ShieldCheck size={18} /> Finalizar
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : step === 'payment' ? (
          <div className="vendas-payment-section">
            <div className="vendas-payment-container">
              {/* BOTÃO COMPACTO PARA SELECIONAR TIPO DE PAGAMENTO */}
              <div className="vendas-payment-type-selector">
                <button 
                  onClick={() => setPaymentTypeModalOpen(true)}
                  className="vendas-payment-type-btn"
                  disabled={loadingSystemPaymentMethods || systemPaymentMethods.length === 0}
                >
                  <Receipt size={18} />
                  <span className="vendas-payment-type-btn-text">
                    {selectedPaymentType === 'DINHEIRO' ? 'Dinheiro' :
                     selectedPaymentType === 'TEF_PAGSEGURO' ? 'TEF PAGSEGURO' :
                     selectedPaymentType === 'PIX_QRCODE' ? 'PIX QRCode' :
                     selectedPaymentType === 'PIX_CNPJ' ? 'PIX CNPJ' :
                     selectedPaymentType === 'DÉBITO' ? 'Cartão de Débito' :
                     selectedPaymentType === 'CRÉDITO_À_VISTA' ? 'Crédito à Vista' :
                     selectedPaymentType === 'CRÉDITO_PARCELADO' ? 'Crédito Parcelado' :
                     selectedPaymentType === 'CREDIÁRIO' ? 'Crediário' :
                     selectedPaymentType === 'CRÉDITO_BÔNUS' ? 'Crédito / Bônus' :
                     'Selecionar Tipo de Pagamento'}
                  </span>
                  <ChevronDown size={14} />
                </button>
              </div>

              {/* INPUT DE VALOR E CONFIGURAÇÃO */}
              {selectedPaymentType && (
                <div className="vendas-payment-input-grid">
                  <div className="vendas-payment-input-card">
                    <label className="vendas-payment-input-label">Valor para este método</label>
                    <div className="vendas-payment-input-wrapper">
                      <span className="vendas-payment-currency">R$</span>
                      <input 
                        type="text" 
                        autoFocus
                        className="vendas-payment-input" 
                        value={currentAmountInput} 
                        placeholder={remainingBalance.toFixed(2).replace('.', ',')}
                        onChange={(e) => {
                          // Formatar entrada monetária
                          let value = e.target.value.replace(/[^\d,]/g, '')
                          const parts = value.split(',')
                          let formatted = parts[0] || ''
                          if (parts.length > 1) {
                            formatted += ',' + parts.slice(1).join('').substring(0, 2)
                          }
                          setCurrentAmountInput(formatted)
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddPayment()}
                      />
                      <button 
                        onClick={handleAddPayment}
                        disabled={parseMoneyBR(currentAmountInput) <= 0 && remainingBalance <= 0}
                        className="vendas-payment-add-btn"
                        title="Registrar este pagamento na venda"
                      >
                        <PlusCircle size={24} />
                      </button>
                    </div>
                  </div>

                  <div className="vendas-payment-card-config">
                    {/* Seletor de Bandeira (para Débito, Crédito à Vista e Crédito Parcelado) */}
                    {(selectedPaymentType === 'DÉBITO' || selectedPaymentType === 'CRÉDITO_À_VISTA' || selectedPaymentType === 'CRÉDITO_PARCELADO') && (
                      <div className="vendas-payment-card-section">
                        <label className="vendas-payment-card-label">Bandeira do Cartão *</label>
                        <div className="vendas-payment-card-brands">
                          {(() => {
                            // Obter bandeiras disponíveis baseado no tipo
                            let availableBrands = []
                            
                            if (selectedPaymentType === 'DÉBITO' && paymentMethodsHierarchy?.DÉBITO) {
                              availableBrands = Object.keys(paymentMethodsHierarchy.DÉBITO)
                            } else if (selectedPaymentType === 'CRÉDITO_À_VISTA' && paymentMethodsHierarchy?.['CRÉDITO_À_VISTA']) {
                              availableBrands = Object.keys(paymentMethodsHierarchy['CRÉDITO_À_VISTA'])
                            } else if (selectedPaymentType === 'CRÉDITO_PARCELADO' && paymentMethodsHierarchy?.['CRÉDITO_PARCELADO']) {
                              availableBrands = Object.keys(paymentMethodsHierarchy['CRÉDITO_PARCELADO'])
                            }
                            
                            // Se não houver bandeiras específicas, usar todas
                            if (availableBrands.length === 0) {
                              availableBrands = ['MASTER', 'VISA', 'ELO', 'AMEX', 'BANRISUL']
                            }
                            
                            return availableBrands.map(brandCode => (
                              <button
                                key={brandCode}
                                onClick={() => {
                                  setSelectedBrand(brandCode)
                                  // Atualizar método selecionado quando mudar bandeira
                                  if (selectedPaymentType === 'DÉBITO') {
                                    const method = findPaymentMethod('DÉBITO', brandCode)
                                    setSelectedMethodId(method?.id || null)
                                  } else if (selectedPaymentType === 'CRÉDITO_À_VISTA') {
                                    const method = findPaymentMethod('CRÉDITO_À_VISTA', brandCode)
                                    setSelectedMethodId(method?.id || null)
                                  } else if (selectedPaymentType === 'CRÉDITO_PARCELADO') {
                                    const method = findPaymentMethod('CRÉDITO_PARCELADO', brandCode, cardInstallments)
                                    setSelectedMethodId(method?.id || null)
                                  }
                                }}
                                className={`vendas-payment-brand-btn ${selectedBrand === brandCode ? 'active' : ''}`}
                              >
                                {getBrandLabel(brandCode)}
                              </button>
                            ))
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Seletor de Parcelas (Apenas Crédito Parcelado) */}
                    {selectedPaymentType === 'CRÉDITO_PARCELADO' && (
                      <div className="vendas-payment-card-section">
                        <label className="vendas-payment-card-label">Número de Parcelas *</label>
                        <div className="vendas-payment-installments-grid">
                          {(() => {
                            // Obter parcelas disponíveis para a bandeira selecionada
                            let availableInstallments = [2, 3, 4, 5, 6, 10, 12]
                            
                            if (selectedBrand && paymentMethodsHierarchy?.['CRÉDITO_PARCELADO']?.[selectedBrand]) {
                              const brandMethods = paymentMethodsHierarchy['CRÉDITO_PARCELADO'][selectedBrand]
                              availableInstallments = Object.keys(brandMethods)
                                .map(key => parseInt(key.replace('x', '')))
                                .filter(n => !isNaN(n))
                                .sort((a, b) => a - b)
                            }
                            
                            return availableInstallments.map(n => (
                              <button 
                                key={n}
                                onClick={() => {
                                  setCardInstallments(n)
                                  // Atualizar método selecionado quando mudar parcelas
                                  if (selectedBrand) {
                                    const method = findPaymentMethod('CRÉDITO_PARCELADO', selectedBrand, n)
                                    setSelectedMethodId(method?.id || null)
                                  }
                                }}
                                className={`vendas-payment-installment-btn ${cardInstallments === n ? 'active' : ''}`}
                              >
                                {n}x
                              </button>
                            ))
                          })()}
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              )}

              {/* LISTA DE PAGAMENTOS ADICIONADOS */}
              <div className="vendas-payment-list-card">
                <div className="vendas-payment-list-header">
                  <span className="vendas-payment-list-title">Pagamentos Vinculados</span>
                  <span className="vendas-payment-list-total">Soma: {formatCurrency(totalPaid)}</span>
                </div>
                <div className="vendas-payment-list-body">
                  {appliedPayments.length === 0 ? (
                    <div className="vendas-payment-list-empty">
                      <Wallet size={48} />
                      <p className="vendas-payment-list-empty-text">Nenhum pagamento registrado</p>
                    </div>
                  ) : (
                    appliedPayments.map(p => {
                      const method = systemPaymentMethods.find(m => m.id === p.paymentReceiptMethodId)
                      const Icon = method ? getPaymentIcon(method) : Wallet
                      const brandLabel = p.brand && method && isCardPaymentType(method.payment_type)
                        ? getBrandLabel(p.brand)
                        : null
                      // Pré-pago no site: valor face (sem MDR local) para não quebrar caixa/conciliação
                      const taxRate = p.isOnlinePrepaid
                        ? 0
                        : (method?.tax_rate ? parseFloat(method.tax_rate) : 0)
                      const taxAmount = taxRate > 0 ? (p.amount * taxRate / 100) : 0
                      const netAmount = Math.round((p.amount - taxAmount) * 100) / 100
                      
                      return (
                        <div key={p.id} className="vendas-payment-list-item">
                          <div className="vendas-payment-list-item-left">
                            <div className="vendas-payment-list-item-icon" style={{
                              backgroundColor: p.brand === 'MASTER' ? '#eb001b' : 
                                               p.brand === 'VISA' ? '#1434cb' :
                                               p.brand === 'ELO' ? '#ffcb05' :
                                               p.brand === 'AMEX' ? '#006fcf' :
                                               p.brand === 'BANRISUL' ? '#fecb00' :
                                               '#e5e7eb',
                              color: p.brand ? 'white' : '#6b7280',
                              fontWeight: 'bold',
                              fontSize: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {p.brand ? p.brand.substring(0, 2).toUpperCase() : <Icon size={18} />}
                            </div>
                            <div style={{ flex: 1 }}>
                              <p className="vendas-payment-list-item-method">
                                {p.methodLabel || method?.name || 'Método não identificado'}
                                {brandLabel && <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>• {brandLabel}</span>}
                              </p>
                              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                                {p.isCrediario && Array.isArray(p.installments) && p.installments.length > 0 && (
                                  <span style={{ fontSize: '0.75rem', color: '#d97706' }}>
                                    {p.installments.length}x crediário
                                    {p.installments[0]?.dueDate && (
                                      <> · 1º venc.{' '}
                                        {new Date(p.installments[0].dueDate + 'T12:00:00').toLocaleDateString('pt-BR')}
                                      </>
                                    )}
                                  </span>
                                )}
                                {!p.isCrediario && typeof p.installments === 'number' && p.installments > 1 && (
                                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                    {p.installments}x
                                  </span>
                                )}
                                {taxRate > 0 && (
                                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                    Taxa: {taxRate.toFixed(2)}%
                                  </span>
                                )}
                              </div>
                              {p.isOnlinePrepaid && (
                                <p style={{ fontSize: '0.7rem', color: '#059669', marginTop: '0.25rem' }}>
                                  Valor face (já liquidado no site) — sem taxa local
                                </p>
                              )}
                              {taxRate > 0 && (
                                <p style={{ fontSize: '0.7rem', color: '#059669', marginTop: '0.25rem' }}>
                                  Líquido: {formatCurrency(netAmount)}
                                </p>
                              )}
                              {/* Campo de Autorização (apenas para métodos que não são dinheiro) */}
                              {p.brand && (
                                <div style={{ marginTop: '0.5rem' }}>
                                  <input
                                    type="text"
                                    value={p.authorizationNumber || ''}
                                    onChange={(e) => handleUpdatePaymentAuthorization(p.id, e.target.value)}
                                    placeholder="Nº de autorização"
                                    className="vendas-payment-input"
                                    style={{ 
                                      width: '100%', 
                                      fontSize: '0.75rem',
                                      padding: '0.375rem 0.5rem',
                                      border: '1px solid #e5e7eb',
                                      borderRadius: '4px'
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="vendas-payment-list-item-right">
                            <div style={{ textAlign: 'right' }}>
                              <span className="vendas-payment-list-item-amount">{formatCurrency(p.amount)}</span>
                              {taxRate > 0 && (
                                <span style={{ 
                                  display: 'block', 
                                  fontSize: '0.7rem', 
                                  color: '#dc2626',
                                  marginTop: '0.25rem'
                                }}>
                                  Taxa: {formatCurrency(taxAmount)}
                                </span>
                              )}
                            </div>
                            <button onClick={() => handleRemovePayment(p.id)} className="vendas-payment-list-item-remove">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            </div>

            {/* LADO DIREITO: RESUMO FINANCEIRO */}
            <div className="vendas-payment-summary-panel">
              <div className="vendas-payment-summary-gradient" />

              <div className="vendas-payment-summary-header">
                <h3 className="vendas-payment-summary-title">
                  <Calculator size={16} /> Resumo
                </h3>
              </div>

              <div className="vendas-payment-summary-content">
                <div className="vendas-payment-summary-row">
                  <span className="vendas-payment-summary-label">Total da Venda</span>
                  <span className="vendas-payment-summary-value">{formatCurrency(totals.finalAmount)}</span>
                </div>
                <div className="vendas-payment-summary-row">
                  <span className="vendas-payment-summary-label">Total Pago</span>
                  <span className="vendas-payment-summary-value">{formatCurrency(totalPaid)}</span>
                </div>
                <div className="vendas-payment-summary-row">
                  <span className="vendas-payment-summary-label">Saldo Restante</span>
                  <span className={`vendas-payment-summary-value ${remainingBalance > 0 ? 'warning' : 'success'}`}>
                    {formatCurrency(remainingBalance)}
                  </span>
                </div>
                {changeAmount > 0 && (
                  <div className="vendas-payment-summary-row">
                    <span className="vendas-payment-summary-label">Troco (pagamento a maior)</span>
                    <span className="vendas-payment-summary-value success">{formatCurrency(changeAmount)}</span>
                  </div>
                )}
                {hasCashPayment && cashReceived != null && (
                  <>
                    <div className="vendas-payment-summary-row">
                      <span className="vendas-payment-summary-label">Valor recebido</span>
                      <span className="vendas-payment-summary-value">{formatCurrency(cashReceived)}</span>
                    </div>
                    <div className="vendas-payment-summary-row">
                      <span className="vendas-payment-summary-label">Troco</span>
                      <span className="vendas-payment-summary-value success">{formatCurrency(cashChangeConfirmed || 0)}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="vendas-payment-summary-footer">
                <button 
                  onClick={() => {
                    setStep('counting')
                    setAppliedPayments([])
                    setCurrentAmountInput('')
                    setSelectedPaymentType(null)
                    setSelectedMethodId(null)
                    setSelectedBrand('MASTER')
                    setCardInstallments(1)
                    clearCashChangeState()
                  }}
                  className="vendas-payment-summary-btn vendas-payment-summary-btn-cancel"
                >
                  <X size={16} /> Voltar
                </button>
                <button 
                  onClick={requestFinishPayment}
                  disabled={loading || !isFullyPaid} 
                  className="vendas-payment-summary-btn vendas-payment-summary-btn-finish"
                  title={finishPaymentDisabledReason || undefined}
                >
                  <ShieldCheck size={18} /> Finalizar Venda
                </button>
                {!isFullyPaid && finishPaymentDisabledReason && (
                  <p className="vendas-payment-summary-hint" role="status">
                    <Info size={14} /> {finishPaymentDisabledReason}
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : step === 'success' ? (
          <div className="vendas-success-section">
            <div className="vendas-success-container">
              <div className="vendas-success-icon-wrapper">
                <Check size={48} strokeWidth={3} />
              </div>
              <h2 className="vendas-success-title">
                Checkout<br/><span className="vendas-success-title-highlight">Finalizado</span>
              </h2>
              <p className="vendas-success-subtitle">Venda finalizada com sucesso</p>
              <div className="vendas-success-actions">
                <button 
                  onClick={openPrintModal}
                  className="vendas-success-btn vendas-success-btn-print"
                >
                  <Printer size={16} /> Cupom / Notas
                </button>
                <button 
                  onClick={resetSale}
                  className="vendas-success-btn vendas-success-btn-next"
                >
                  <RefreshCcw size={16} /> Próximo Atendimento
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* LOADING OVERLAY */}
      {(loading || closingCashRegister) && (
        <div className="vendas-loading-overlay">
          <div className="vendas-loading-container">
            <div className="vendas-loading-spinner-large" />
            <Loader2 className="vendas-loading-icon-large" size={40} />
          </div>
          <p className="vendas-loading-text-large">
            {closingCashRegister ? 'Fechando Caixa...' : (loadingStep || 'Processando Venda...')}
          </p>
        </div>
      )}

      {/* Modal de Confirmação */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        showCancel={confirmModal.showCancel}
      />

      <CrediarioPaymentModal
        isOpen={crediarioModalOpen}
        onClose={() => {
          setCrediarioModalOpen(false)
          setPendingCrediarioPayment(null)
        }}
        amount={pendingCrediarioPayment?.amount || 0}
        onConfirm={handleConfirmCrediarioPayment}
      />

      {cashChangeModalOpen && (
        <div
          className="vendas-modal-overlay"
          onClick={() => setCashChangeModalOpen(false)}
        >
          <div
            className="vendas-modal-content vendas-modal-cash-change"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="vendas-modal-header">
              <div className="vendas-modal-header-left">
                <div className="vendas-modal-icon" style={{ backgroundColor: '#d1fae5', color: '#059669' }}>
                  <Banknote size={20} />
                </div>
                <div>
                  <h2 className="vendas-modal-title">Troco em Dinheiro</h2>
                  <p className="vendas-modal-subtitle">Informe o valor que o cliente entregou</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCashChangeModalOpen(false)}
                className="vendas-modal-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="vendas-cash-change-body">
              <div className="vendas-cash-change-row">
                <span>Total da venda</span>
                <strong>{formatCurrency(totals.finalAmount)}</strong>
              </div>
              <div className="vendas-cash-change-row">
                <span>Total em dinheiro</span>
                <strong>{formatCurrency(cashPaidTotal)}</strong>
              </div>

              <label className="vendas-cash-change-label" htmlFor="vendas-cash-received">
                Valor recebido do cliente
              </label>
              <div className="vendas-cash-change-input-wrapper">
                <span className="vendas-cash-change-currency">R$</span>
                <input
                  id="vendas-cash-received"
                  type="text"
                  autoFocus
                  inputMode="decimal"
                  className="vendas-cash-change-input"
                  value={cashReceivedInput}
                  placeholder={formatMoneyInputBR(cashPaidTotal)}
                  onChange={(e) => setCashReceivedInput(formatCashReceivedInput(e.target.value))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleConfirmCashChange()
                  }}
                />
              </div>

              <div className={`vendas-cash-change-result ${cashChangePreview > 0 ? 'has-change' : ''}`}>
                <span>Troco</span>
                <strong>{formatCurrency(cashChangePreview)}</strong>
              </div>
            </div>

            <div className="vendas-cash-change-actions">
              <button
                type="button"
                className="vendas-modal-closing-btn vendas-modal-closing-btn-cancel"
                onClick={() => setCashChangeModalOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="vendas-modal-closing-btn vendas-modal-closing-btn-confirm"
                onClick={handleConfirmCashChange}
                disabled={cashReceivedPreview + MONEY_EPSILON < cashPaidTotal}
              >
                Confirmar e Finalizar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default VendasPage

